import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';


import { Loader2, CheckCircle, AlertCircle, Image as ImageIcon, Sparkles, Copy, Save, Lightbulb, Upload, ArrowRight } from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useModal } from '../../components/ui/ModalProvider';
import { supabase } from '../../lib/supabase';
import * as openaiService from '../../services/openaiService';
import * as imageService from '../../services/imageService';
import { downloadAndStoreFromUrl } from '../../services/imageService';
import { getSites, getAllPrompts, saveDraftArticle, getDraftArticles, deleteDraftArticle, fetchGymPlixCategories } from '../../services/wordpressService';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuthSessionHeaders } from '../../services/sessionService';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';
import { getCopyBlogsQueueKey } from '../../utils/copyBlogsQueue';




import { wordpressService } from '../../services/wordpressService';



// Always use GymPlix connector categories instead of WordPress default REST
async function fetchWordPressCategories(siteId) {
  try {
    // Validate siteId
    if (!siteId) {
      return {
        success: false,
        error: 'Site not selected',
        categories: []
      };
    }

    // Use GymPlix connector first (token-based)
    const result = await fetchGymPlixCategories(siteId, { backgroundRefresh: true });

    if (!result?.success) {
      // Fallback to native WordPress REST (Basic Auth)
      const fallbackCategories = await wordpressService?.fetchWordPressCategories(siteId);
      if (Array.isArray(fallbackCategories) && fallbackCategories?.length > 0) {
        return {
          success: true,
          categories: fallbackCategories?.map((cat) => ({
            id: cat?.id,
            name: cat?.name,
            slug: cat?.slug
          }))
        };
      }

      return {
        success: false,
        error: result?.error || 'Could not load categories',
        categories: []
      };
    }

    // Normalize into the UI format:
    return {
      success: true,
      categories: result?.categories?.map((cat) => ({
        id: cat?.id,
        name: cat?.name,
        slug: cat?.name?.toLowerCase()?.replace(/\s+/g, '-')
      }))
    };

  } catch (err) {
    return {
      success: false,
      error: err?.message,
      categories: []
    };
  }
}

function generateSlugFromKeyword(focusKeyword, existingSlugs = []) {
  const baseSlug = (focusKeyword || '')
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s-]/g, '')
    ?.replace(/\s+/g, '-')
    ?.replace(/-+/g, '-')
    ?.replace(/^-|-$/g, '');

  let counter = 1;
  let slug = `${baseSlug}-${counter}`;
  while (existingSlugs?.includes(slug)) {
    counter += 1;
    slug = `${baseSlug}-${counter}`;
  }
  return slug;
}

const POWER_WORDS = [
  'Best', 'Easy', 'Quick', 'Delicious', 'Ultimate', 'Perfect', 'Simple', 'Healthy',
  'Amazing', 'Tasty', 'Flavorful', 'Crispy', 'Creamy', 'Budget', 'Fast'
];

const STOP_WORDS = new Set([
  'the', 'and', 'or', 'of', 'with', 'for', 'to', 'a', 'an', 'in', 'on', 'at', 'from'
]);

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'EN - English' },
  { value: 'de', label: 'DE - German' },
  { value: 'pl', label: 'PL - Polish' },
  { value: 'fr', label: 'FR - French' },
  { value: 'es', label: 'ES - Spanish' }
];

function deriveFocusKeywordFromText(text) {
  // \p{L}/\p{N} (Unicode letter/number) instead of a[-zA-Z0-9] so accented
  // letters (ą, ę, ł, ö, ü, é, ñ, etc.) survive instead of being stripped to
  // mangled fragments — this text is often already in the profile's language.
  const cleaned = (text || '')
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .replace(/\d+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const words = cleaned
    .split(' ')
    .filter(Boolean)
    .filter((w) => !STOP_WORDS.has(w) && !POWER_WORDS.map((p) => p.toLowerCase()).includes(w));

  return words.slice(0, 4).join(' ');
}

// Recipe-site word bank for the 5 supported language profiles, used only as
// a last-resort local fallback when the AI edge call fails or returns
// nothing — so a failure never silently produces English text under a
// non-English profile. Real generation always prefers the AI edge result;
// these templates are the safety net, not the primary path.
const LOCALIZED_RECIPE_COPY = {
  en: {
    recipeWord: 'Recipe',
    titlePatterns: (kw) => [`Best ${kw} Recipe`, `Easy ${kw} Recipe`, `Homemade ${kw}`, `Quick ${kw} Recipe`, `The Best ${kw}`],
    metaSentence: (kw) => `Try this easy ${kw} recipe that's simple to make and full of flavor. Perfect for any occasion — ready in minutes with ingredients you already have!`
  },
  pl: {
    recipeWord: 'Przepis',
    titlePatterns: (kw) => [`Najlepszy Przepis na ${kw}`, `Łatwy Przepis na ${kw}`, `Domowy ${kw}`, `Szybki Przepis na ${kw}`, `Sprawdzony Przepis na ${kw}`],
    metaSentence: (kw) => `Wypróbuj ten łatwy przepis na ${kw}, prosty w przygotowaniu i pełen smaku. Idealny na każdą okazję — gotowy w kilka minut ze składnikami, które już masz!`
  },
  de: {
    recipeWord: 'Rezept',
    titlePatterns: (kw) => [`Bestes ${kw} Rezept`, `Einfaches ${kw} Rezept`, `Hausgemachtes ${kw}`, `Schnelles ${kw} Rezept`, `Klassisches ${kw} Rezept`],
    metaSentence: (kw) => `Probiere dieses einfache ${kw} Rezept, das schnell zubereitet und voller Geschmack ist. Perfekt für jeden Anlass — in wenigen Minuten fertig mit Zutaten, die du bereits hast!`
  },
  fr: {
    recipeWord: 'Recette',
    titlePatterns: (kw) => [`Meilleure Recette de ${kw}`, `Recette Facile de ${kw}`, `${kw} Fait Maison`, `Recette Rapide de ${kw}`, `${kw} Traditionnelle`],
    metaSentence: (kw) => `Essayez cette recette facile de ${kw}, simple à préparer et pleine de saveur. Parfaite pour toute occasion — prête en quelques minutes avec des ingrédients que vous avez déjà !`
  },
  es: {
    recipeWord: 'Receta',
    titlePatterns: (kw) => [`Mejor Receta de ${kw}`, `Receta Fácil de ${kw}`, `${kw} Casero`, `Receta Rápida de ${kw}`, `${kw} Tradicional`],
    metaSentence: (kw) => `Prueba esta receta fácil de ${kw}, sencilla de preparar y llena de sabor. Perfecta para cualquier ocasión: ¡lista en minutos con ingredientes que ya tienes!`
  }
};

function getLocalizedCopy(language) {
  return LOCALIZED_RECIPE_COPY[language] || LOCALIZED_RECIPE_COPY.en;
}

// Deterministic-but-varied local title fallback for non-English profiles —
// mirrors buildRankMathTitle's hashing approach but never mixes in English
// words, since buildRankMathTitle's context patterns are English-only.
function buildLocalizedTitle(focusKeyword, language, articleIndex = 0) {
  const raw = (focusKeyword || getLocalizedCopy(language).recipeWord).trim();
  const keyword = raw.replace(/\b\w/g, (c) => c.toUpperCase());
  const patterns = getLocalizedCopy(language).titlePatterns(keyword);
  const hash = raw.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const pick = patterns[(hash + articleIndex * 7) % patterns.length];
  return trimTitleToMax(pick || keyword, 65);
}

function ensurePowerWord(title) {
  const hasPowerWord = POWER_WORDS.some((word) => new RegExp(`\\b${word}\\b`, 'i').test(title));
  if (hasPowerWord) return title;
  return title.replace(/^\d+\s+/, (match) => `${match}Best `);
}

function normalizeTitle(title, focusKeyword) {
  let t = (title || focusKeyword || 'Recipe').trim();
  const keyword = (focusKeyword || '').trim();

  // Ensure keyword appears in the title
  if (keyword && !t.toLowerCase().includes(keyword.toLowerCase())) {
    t = `${keyword} ${t}`.trim();
  }

  // Move keyword to the front if it isn't there already
  if (keyword && !t.toLowerCase().startsWith(keyword.toLowerCase())) {
    const re = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    t = `${keyword} ${t.replace(re, '').replace(/\s{2,}/g, ' ').trim()}`.trim();
  }

  // Add a descriptive power word if none present
  if (!POWER_WORDS.some((w) => new RegExp(`\\b${w}\\b`, 'i').test(t))) {
    if (/healthy|light|vegan|vegetarian/i.test(t)) {
      t = `Healthy ${t}`;
    } else if (/creamy|cheese|cheesy/i.test(t)) {
      t = `Creamy ${t}`;
    } else if (/crispy|crunchy/i.test(t)) {
      t = `Crispy ${t}`;
    } else if (/quick|fast/i.test(t)) {
      t = `Quick ${t}`;
    } else {
      t = `Easy ${t}`;
    }
  }

  return trimTitleToMax(t, 65);
}

function buildSEOTitle(baseTitle, focusKeyword, language = 'en') {
  const keyword = (focusKeyword || baseTitle || '').trim();
  // buildRankMathTitle's context patterns ("Cheesy", "Best ... Recipe", etc.)
  // are English-only word banks — using them under a non-English profile is
  // what produced titles mixing the keyword's language with English filler.
  if (language !== 'en') {
    return buildLocalizedTitle(keyword, language);
  }
  return buildRankMathTitle(keyword);
}

// ============================================================
// SEO ARTICLE TITLE BUILDER
// Generates high-quality, context-aware SEO titles using the focus keyword.
// Rules: 45-65 chars, keyword near start, match recipe context, no number lists.
// ============================================================
function buildRankMathTitle(focusKeyword, articleIndex = 0) {
  const raw = (focusKeyword || 'Recipe').trim();
  // Title-case the keyword so it reads naturally inside any pattern
  const keyword = raw.replace(/\b\w/g, c => c.toUpperCase());
  const kw = raw.toLowerCase();

  // Detect recipe context from keyword
  const isCheesy        = /chees|gratin|parmesan|mozzarella|cheddar|brie|gouda/.test(kw);
  const isCreamy        = /cream|alfredo|bechamel|bisque|chowder/.test(kw);
  const isCrispy        = /crisp|crunch|\bfried\b|\bfry\b/.test(kw);
  const isQuick         = /quick|fast/.test(kw);
  const isHealthy       = /health|light|vegan|vegetar|salad|fresh|low.cal|low.carb/.test(kw);
  const isComfort       = /\bsoup\b|stew|casserole|gratin|\bpot pie\b|meatball|meatloaf|chili/.test(kw);
  const hasGarlic       = /garlic/.test(kw);
  const isBaked         = /\bbak|gratin|casserole|roast/.test(kw);

  // Context-specific patterns (highest priority)
  const contextPool = [];
  if (isCheesy)  contextPool.push(
    `Easy Cheesy ${keyword}`, `Cheesy ${keyword} Recipe`,
    `Ultimate Cheesy ${keyword}`, `Creamy ${keyword} Recipe`, `Best Cheesy ${keyword}`
  );
  if (isCreamy)  contextPool.push(
    `Creamy ${keyword} Recipe`, `Easy Creamy ${keyword}`,
    `The Best Creamy ${keyword}`, `Creamy Homemade ${keyword}`
  );
  if (isCrispy)  contextPool.push(
    `Crispy ${keyword} Recipe`, `Extra Crispy ${keyword}`,
    `Perfectly Crispy ${keyword}`, `Golden ${keyword} Recipe`
  );
  if (isHealthy) contextPool.push(
    `Healthy ${keyword} Recipe`, `Light ${keyword} Recipe`,
    `Easy Healthy ${keyword}`, `Fresh & Easy ${keyword}`
  );
  if (isQuick)   contextPool.push(
    `Quick & Easy ${keyword}`, `30-Minute ${keyword} Recipe`, `Fast ${keyword} Recipe`
  );
  if (isComfort) contextPool.push(
    `Homemade ${keyword} Recipe`, `Classic ${keyword} Recipe`,
    `Cozy ${keyword} Recipe`, `Comforting ${keyword}`, `Family Favorite ${keyword}`
  );
  // Garlic-prefix patterns only when keyword doesn't already mention garlic
  if (!hasGarlic) contextPool.push(
    `Garlic Parmesan ${keyword}`, `Garlic Butter ${keyword}`,
    `Cheesy Garlic ${keyword}`, `Creamy Garlic ${keyword}`
  );
  // Baked-prefix only when keyword doesn't already lead with a bake word
  if (isBaked && !kw.startsWith('bak')) contextPool.push(
    `Easy Baked ${keyword}`, `Baked ${keyword} Recipe`, `Perfect Baked ${keyword}`
  );

  // Universal patterns (always available)
  const universal = [
    `Best ${keyword} Recipe`,
    `Easy ${keyword} Recipe`,
    `The Best ${keyword}`,
    `Homemade ${keyword}`,
    `Perfect ${keyword} Recipe`,
    `Simple ${keyword} Recipe`,
    `Easy Homemade ${keyword}`,
    `Best Ever ${keyword}`,
    `How to Make ${keyword}`,
    `Classic ${keyword}`,
    `The Ultimate ${keyword}`,
    `Quick & Easy ${keyword}`,
    `Flavorful ${keyword}`,
    `Family-Friendly ${keyword}`,
    `Restaurant-Style ${keyword}`,
    `Mouthwatering ${keyword}`,
    `Irresistible ${keyword}`,
    `Must-Try ${keyword}`,
    `No-Fail ${keyword} Recipe`,
    `Delicious ${keyword}`,
    `${keyword} for Dinner Tonight`,
    `${keyword} for the Whole Family`,
    `${keyword}: Easy & Delicious`,
    `${keyword} Everyone Loves`,
    `${keyword} Your Family Will Love`,
    `${keyword} from Scratch`,
    `${keyword}: A Family Favorite`,
    `Cozy ${keyword} Recipe`,
    `Kid-Friendly ${keyword}`,
    `Foolproof ${keyword} Recipe`,
    `How to Make the Best ${keyword}`,
  ];

  // Remove titles where a significant word (>3 chars) appears more than once
  const noRepeat = (title) => {
    const words = title.toLowerCase().split(/\s+/);
    return !words.some((w, i) => w.length > 3 && words.indexOf(w) !== i);
  };

  const combined = [...contextPool, ...universal].filter(noRepeat);

  // Prefer ideal SEO length 45-65 chars; fall back to anything ≤65
  const ideal      = combined.filter(t => t.length >= 45 && t.length <= 65);
  const acceptable = combined.filter(t => t.length <= 65);
  const candidates = ideal.length >= 3 ? ideal : (acceptable.length > 0 ? acceptable : combined);

  // Deterministic but varied pick: hash from raw keyword + spread by article index
  const hash = raw.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const pick = candidates[(hash + articleIndex * 7) % candidates.length];

  return pick || `Best ${keyword} Recipe`;
}

function generateMetaDescriptionLocal(title, focusKeyword, language = 'en') {
  const copy = getLocalizedCopy(language);
  const keyword = (focusKeyword || copy.recipeWord.toLowerCase()).trim();
  const text = copy.metaSentence(keyword).trim();
  return safeMetaTrim(text);
}

// Cut meta description safely: prefer sentence boundary, then word boundary, never mid-word
function safeMetaTrim(text, max = 155) {
  let t = (text || '').trim().replace(/\.\.\.+$/, '.').replace(/\s+-\s*$/, '.');
  if (t.length <= max) return t.endsWith('.') ? t : `${t}.`;
  // Try to cut at last sentence boundary (. ! ?) within max
  const sentenceEnd = /[.!?]/g;
  let lastSentence = -1;
  let m;
  const slice = t.substring(0, max);
  while ((m = sentenceEnd.exec(slice)) !== null) lastSentence = m.index;
  if (lastSentence > 80) return t.substring(0, lastSentence + 1).trim();
  // Fallback: cut at last word boundary
  const lastSpace = slice.lastIndexOf(' ');
  t = (lastSpace > 80 ? slice.substring(0, lastSpace) : slice).trim();
  return t.endsWith('.') ? t : `${t}.`;
}

function trimTitleToMax(title, max = 65) {
  let t = (title || '').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  t = (lastSpace > 20 ? cut.slice(0, lastSpace) : cut).trim();
  // Never let a safety-net trim end the title on a dangling short connector
  // word (a/the/of/and/i/w/na/de/le/un/der/die/...) — that's what "cut off
  // mid-phrase" looks like even when it isn't cut mid-word.
  const trailingWord = t.match(/\s(\p{L}{1,3})$/u);
  if (trailingWord) {
    t = t.slice(0, t.length - trailingWord[0].length).trim();
  }
  return t;
}

function normalizeMetaDescription(meta, title, focusKeyword, language = 'en') {
  const keyword = (focusKeyword || 'recipe').trim();
  let text = (meta || '').trim();

  // If AI gave nothing, generate locally — in the same profile language,
  // never English filler under a non-English profile.
  if (!text) {
    return generateMetaDescriptionLocal(title, keyword, language);
  }

  // Ensure keyword is present (prepend only if absent and won't cause repetition)
  if (keyword && !text.toLowerCase().includes(keyword.toLowerCase())) {
    text = `${keyword} - ${text}`;
  }

  // Safe trim: sentence boundary first, then word boundary, never mid-word
  return safeMetaTrim(text, 155);
}

function generateShortDescriptionLocal(title, focusKeyword) {
  const baseTitle = (title || 'Recipe').trim();
  const keyword = (focusKeyword || 'recipe').trim();
  let text = `${baseTitle} is a simple, flavorful ${keyword} recipe with easy steps and great taste.`;
  if (text.length > 160) {
    text = text.substring(0, 160);
  }
  return text;
}

// Fields required to send an article to the Blog Posts Queue. Checked at
// send time (both per-article and "Send All") instead of gating the page's
// bulk actions behind a fixed generation order.
const REQUIRED_QUEUE_FIELDS = [
  { label: 'Title', check: (a) => !!a?.title?.trim?.() },
  { label: 'Focused Keyword', check: (a) => !!a?.focusKeyword?.trim?.() },
  { label: 'Category', check: (a) => !!a?.selectedCategory },
  { label: 'Meta Description', check: (a) => !!a?.metaDescription?.trim?.() },
  { label: 'Main Image', check: (a) => !!a?.image_main_url },
];

function getMissingQueueFields(article) {
  return REQUIRED_QUEUE_FIELDS.filter((f) => !f.check(article)).map((f) => f.label);
}

async function generateTitlesFromEdge(keyword, language) {
  const { data, error } = await supabase?.functions?.invoke('generate-titles', {
    body: { keyword, language },
    headers: await getAuthSessionHeaders()
  });
  if (error) throw error;
  return data?.titles || [];
}

async function generateFocusKeywordFromEdge(keyword) {
  const { data, error } = await supabase?.functions?.invoke('generate-focus-keyword', {
    body: { keyword },
    headers: await getAuthSessionHeaders()
  });
  if (error) throw error;
  return data?.keyword || data?.focusKeyword || '';
}

async function generateMetaDescriptionFromEdge(payload) {
  const { data, error } = await supabase?.functions?.invoke('generate-meta-description', {
    body: payload,
    headers: await getAuthSessionHeaders()
  });
  if (error) throw error;
  return data?.metaDescription || data?.description || '';
}

async function generateSeoBulkFromEdge(items, language) {
  const { data, error } = await supabase?.functions?.invoke('generate-seo-bulk', {
    body: { items, language },
    headers: await getAuthSessionHeaders()
  });
  if (error) throw error;
  return data?.items || [];
}

async function getUserId() {
  const { data: { user } } = await supabase?.auth?.getUser();
  return user?.id || null;
}

function extractTrailingNumber(slug) {
  const match = slug?.match(/[-_](\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

async function getNextGlobalSlugNumberForUser(userId) {
  if (!userId) return 1;
  const { data, error } = await supabase
    ?.from('wp_articles')
    ?.select('permalink')
    ?.eq('user_id', userId);
  if (error || !Array.isArray(data)) return 1;

  let max = 0;
  data.forEach((row) => {
    const n = extractTrailingNumber(row?.permalink);
    if (n && n > max) max = n;
  });
  return max + 1;
}

async function generateSlugForUser(focusKeyword, existingSlugs = [], userId) {
  const baseSlug = (focusKeyword || '')
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s-]/g, '')
    ?.replace(/\s+/g, '-')
    ?.replace(/-+/g, '-')
    ?.replace(/^-|-$/g, '');

  let nextNumber = await getNextGlobalSlugNumberForUser(userId);
  let slug = `${baseSlug}-${nextNumber}`;
  while (
    (existingSlugs?.has && existingSlugs.has(slug)) ||
    (existingSlugs?.includes && existingSlugs.includes(slug))
  ) {
    nextNumber += 1;
    slug = `${baseSlug}-${nextNumber}`;
  }
  return slug;
}

function pickBestTitle(titles, focusKeyword, language = 'en') {
  if (!Array.isArray(titles) || titles.length === 0) {
    return buildSEOTitle(focusKeyword, focusKeyword, language);
  }
  const scored = titles.map((t) => {
    const title = (t || '').trim();
    // No bonus/penalty based on digit presence — a relevant number is a
    // plus, not a minus, per the "one number when appropriate" guidance
    // already given to the AI.
    const score =
      (POWER_WORDS.some((w) => new RegExp(`\\b${w}\\b`, 'i').test(title)) ? 2 : 0) +
      ((title.length >= 50 && title.length <= 60) ? 3 : title.length <= 65 ? 1 : 0) +
      ((focusKeyword && title.toLowerCase().includes(focusKeyword.toLowerCase())) ? 2 : 0);
    return { title, score };
  });
  scored.sort((a, b) => b.score - a.score);
  const best = (scored[0]?.title || titles[0] || '').trim();
  // Safety net only — the AI is already asked to land in the 50-60/65-max
  // range, so this should rarely trigger.
  return trimTitleToMax(best, 65);
}

function hasPorkOrAlcohol(article) {
  const text = [
    article?.title,
    article?.focusKeyword,
    article?.metaDescription,
    article?.shortDescription
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const porkWords = [
    'pork', 'bacon', 'ham', 'pancetta', 'prosciutto', 'lard', 'pork belly', 'chorizo',
    'salami', 'pepperoni', 'pork sausage', 'ribs', 'pork chops'
  ];
  const alcoholWords = [
    'alcohol', 'wine', 'beer', 'vodka', 'rum', 'whiskey', 'whisky', 'bourbon',
    'gin', 'tequila', 'champagne', 'brandy', 'liqueur', 'cider', 'liquor'
  ];

  const matchesWord = (w) => new RegExp(`\\b${w}\\b`, 'i').test(text);
  return porkWords.some(matchesWord) || alcoholWords.some(matchesWord);
}

// Helper function to create an empty article object
function makeEmptyArticle() {
  return {
    _uid: crypto.randomUUID(),
    id: null,
    focusKeyword: '',
    title: '',
    shortDescription: '',
    enableRecipeCard: true,
    seoTitle: '',
    permalink: '',
    metaDescription: '',
    secondaryKeywords: [],
    tags: [],
    status: null,
    wpPostId: null,
    wpPostUrl: null,
    errorMessage: null,
    siteId: null,
    isFromCopyBlogs: false,
    selectedCategory: null,
    wordpressCategory: '',
    wordpressCategoryId: null
  };
}

// Turns a raw Copy Blogs queue entry (from the profile-scoped localStorage
// queue) into an article block, tagged with the profile it was loaded for
// so a later profile switch can tell it apart from the newly active one.
function mapCopyBlogsQueueEntry(imported, profileId) {
  return {
    _uid:             crypto.randomUUID(),
    id:               null,
    focusKeyword:     imported?.focusKeyword || '',
    title:            imported?.title || '',
    shortDescription: imported?.shortDescription || '',
    referenceImageUrl: imported?.referenceImageUrl || '',
    enableRecipeCard: true,
    seoTitle:         imported?.seoTitle || '',
    permalink:        imported?.permalink || '',
    metaDescription:  imported?.metaDescription || '',
    secondaryKeywords: imported?.secondaryKeywords || [],
    tags:             imported?.tags || [],
    category:         imported?.category || '',
    status:           null,
    wpPostId:         null,
    wpPostUrl:        null,
    errorMessage:     null,
    siteId:           null,
    isFromCopyBlogs:  true,
    sourceArticleUrl: imported?.sourceArticleUrl,
    profileId,
  };
}

// Same "does this block have anything worth keeping" signal already used by
// the mount-time cache restore, reused to decide whether a lone untouched
// default block can be silently swapped out on a profile switch.
function articleHasContent(a) {
  return !!(a?.focusKeyword || a?.title || a?.image_main_url || a?.image_top_url || a?.image_middle_url || a?.image_end_url);
}

// Article Block Component
const ArticleBlock = forwardRef(function ArticleBlock(
  { article, index, onUpdate, onDelete, sites, prompts, isGenerating, selectedSite, articlesLength, existingSlugs, sharedCategories, sharedCategoriesLoading, mjGlobalBusy, onMJBusyChange, selectedLanguage, onSendToQueue, onApplyMJResult },
  ref
) {
  const { alert } = useModal();

  // ✅ Per-article "Send to Queue" loading state
  const [isSendingToQueue, setIsSendingToQueue] = useState(false);
  const handleSendToQueue = async () => {
    if (isSendingToQueue) return;
    setIsSendingToQueue(true);
    try {
      await onSendToQueue?.(article?._uid);
    } catch {
      // Parent already surfaces the error via alert(); nothing further to do here.
    } finally {
      setIsSendingToQueue(false);
    }
  };

  // Use shared categories from parent instead of local state
  const [categories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(article?.selectedCategory || null);

  // ✅ ADD MISSING images STATE
  const [images, setImages] = useState({
    main: null,
    top: null,
    middle: null,
    end: null
  });
  
  const [imagePreviews, setImagePreviews] = useState({
    main: article?.image_main_url || null,
    top: article?.image_top_url || null,
    middle: article?.image_middle_url || null,
    end: article?.image_end_url || null
  });

  // Safety net: if image URLs arrive (or change) after mount — e.g. when the parent's
  // state is updated from a cache restore — pull them into the local preview state.
  // Only fills in NULL slots; never overrides a preview that's already been set
  // (so in-progress MidJourney downloads are not clobbered).
  useEffect(() => {
    const hasUrls = article?.image_main_url || article?.image_top_url ||
                    article?.image_middle_url || article?.image_end_url;
    if (!hasUrls) return;
    setImagePreviews(prev => ({
      main:   prev.main   || article?.image_main_url   || null,
      top:    prev.top    || article?.image_top_url    || null,
      middle: prev.middle || article?.image_middle_url || null,
      end:    prev.end    || article?.image_end_url    || null,
    }));
  }, [article?.image_main_url, article?.image_top_url, article?.image_middle_url, article?.image_end_url]);

  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

// ✅ Midjourney state
const [mjPrompt, setMJPrompt] = useState({
  referenceImage: article?.referenceImageUrl || '',
  aspectRatio: '1:1',
  generatedPrompt: ''
});
const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
const [isCopied, setIsCopied] = useState(false);
const [mjSendState, setMJSendState] = useState('idle'); // idle | sending | processing | done | error
const [mjProgress, setMJProgress] = useState(0);
const [mjResultUrl, setMJResultUrl] = useState('');
const mjPollRef = useRef(null);
const mjLastSentRef = useRef(0); // timestamp of last send, for 7s cooldown
const mjBatchIdRef = useRef(''); // unique ID per send — binds this article's images
const mjSeedRef = useRef(0);    // --seed value echoed by MidJourney; used for exact message matching

// ✅ NEW: State for SEO title and focus keywords generation
const [isGeneratingFocusKeywords, setIsGeneratingFocusKeywords] = useState(false);
const showPorkAlcoholWarning = hasPorkOrAlcohol(article);

// ✅ DEFINE FUNCTIONS FIRST (NO "..." placeholders)

// NEW: Generate 10 Focus Keywords (primary + secondary)
const handleGenerate10FocusKeywords = async () => {
  if (!article?.focusKeyword?.trim() || !article?.title?.trim()) {
    return;
  }

  setIsGeneratingFocusKeywords(true);
  try {
    const keywords = await openaiService?.generate10FocusKeywords(
      article?.focusKeyword,
      article?.title
    );
    
    if (keywords) {
      // Store as comma-separated string for WordPress tags
      onUpdate(index, { generatedFocusKeywords: keywords }, false);
    }
  } catch (error) {
    console.error('Focus keywords generation error:', error);
  } finally {
    setIsGeneratingFocusKeywords(false);
  }
};

// ============================================
// FIXED CATEGORIES AND RULES FOR AUTO-CATEGORIZATION
// ============================================

// Define fixed main categories with keywords
const FIXED_CATEGORIES = {
  dessert: {
    id: 'dessert',
    name: 'Dessert',
    keywords: ['cake', 'cakes', 'cookie', 'cookies', 'brownie', 'brownies', 'cupcake', 'cupcakes', 'cheesecake', 'tart', 'tarts', 'pie', 'pies', 'ice cream', 'pudding', 'custard', 'mousse', 'pastry', 'pastries', 'bars', 'no-bake', 'sweet', 'candy', 'chocolate', 'fudge', 'trifle', 'souffle', 'crumble', 'cobbler', 'doughnuts', 'donuts', 'macarons', 'tiramisu', 'panna cotta', 'brittle', 'confection']
  },
  breakfast: {
    id: 'breakfast',
    name: 'Breakfast',
    keywords: ['pancake', 'pancakes', 'waffle', 'waffles', 'crepe', 'crepes', 'oatmeal', 'porridge', 'granola', 'muesli', 'cereal', 'eggs', 'benedict', 'hash', 'breakfast bowl', 'smoothie bowl', 'acai', 'yogurt', 'parfait', 'muffin', 'muffins', 'scramble', 'frittata', 'omelette', 'quiche', 'breakfast sandwich', 'breakfast burrito', 'biscuits', 'gravy']
  },
  lunch: {
    id: 'lunch',
    name: 'Lunch',
    keywords: ['rice bowl', 'pasta dish', 'chicken breast', 'beef', 'fish', 'salmon', 'shrimp', 'one-pan', 'one pan', 'meal-prep', 'meal prep', 'high-protein', 'protein', 'wrap', 'burrito', 'sandwich', 'sub', ' gyro', 'kebab', 'skewer', 'bowl meal', 'grain bowl', 'power bowl', ' Cobb', 'Caesar', 'club sandwich', 'panini', 'croque', 'quesadilla', 'nachos', 'taco', 'tacos', 'empanada', 'calzone', 'pita', 'lunch plate']
  },
  dinner: {
    id: 'dinner',
    name: 'Dinner',
    keywords: ['soup', 'soups', 'stew', 'stews', 'curry', 'casseroles', 'casserole', 'comfort food', 'family meal', 'roast', 'roasted', 'baked', 'braised', 'braise', 'simmered', 'poached', 'grilled', 'bbq', 'barbecue', 'stir-fry', 'stir fry', 'fried rice', 'noodles', 'lasagna', 'bolognese', 'alfredo', 'carbonara', 'risotto', 'paella', 'gumbo', 'jambalaya', 'chili', 'hungry', 'filling', 'hearty', 'substantial']
  },
  snacks: {
    id: 'snacks',
    name: 'Snacks',
    keywords: ['energy ball', 'energy balls', 'snack bar', 'granola bar', 'protein bar', 'quick bite', 'finger food', 'crackers', 'chips', 'pretzel', 'pretzels', 'popcorn', 'nuts', 'trail mix', 'mix', 'hummus', 'veggie sticks', 'salsa', 'guacamole', 'dip', 'dips', 'cheese', 'charcuterie', 'appetizer', 'starter']
  },
  drinks: {
    id: 'drinks',
    name: 'Drinks & Juices',
    keywords: ['juice', 'juices', 'smoothie', 'smoothies', 'shake', 'shakes', 'detox', 'lemonade', 'iced tea', 'coffee', 'espresso', 'latte', 'cappuccino', 'mocha', 'hot chocolate', 'cocktail', 'mocktail', 'beverage', 'water', 'infused', 'slushie', 'slushies', 'frappe', 'milkshake', 'ade']
  },
  bread: {
    id: 'bread',
    name: 'Bread & Baking',
    keywords: ['bread', 'rolls', 'buns', 'roll', 'bun', 'flatbread', 'naan', 'pita', 'focaccia', 'ciabatta', 'baguette', 'sourdough', 'yeast', 'fermented', 'homemade bakery', 'crackers', 'breadsticks', 'pizza dough', 'dough']
  },
  salads: {
    id: 'salads',
    name: 'Salads',
    keywords: ['salad', 'salads', 'greens', 'garden salad', 'caesar', 'greek', 'waldorf', 'potato salad', 'macaroni salad', 'pasta salad', 'rice salad', 'quinoa salad', 'couscous', 'tabbouleh', 'coleslaw', 'leafy', 'vegetable salad', 'protein salad', 'chicken salad', 'tuna salad', 'egg salad', 'seafood salad', 'wedge']
  },
  appetizers: {
    id: 'appetizers',
    name: 'Appetizers & Starters',
    keywords: ['appetizer', 'starter', 'bruschetta', 'caprese', 'bruschetta', 'dip', 'dips', 'fondue', 'mozzarella', 'jalapeno', 'poppers', 'spring roll', 'egg roll', 'wings', 'nachos', ' Loaded', 'party', 'celebration', 'reception', 'reception', 'cocktail party', 'finger food', 'canape', 'canapes', 'sliders', 'mini', 'bites']
  },
  sauces: {
    id: 'sauces',
    name: 'Sauces & Condiments',
    keywords: ['sauce', 'sauces', 'dressing', 'vinaigrette', 'mayonnaise', 'aioli', 'pesto', 'chimichurri', 'marinade', 'rub', 'glaze', 'gravy', 'salsa', 'chutney', 'relish', 'jam', 'jelly', 'butter', 'compound butter', 'spread', 'condiment', 'dip mix', 'seasoning', 'spice blend']
  }
};

// Special diet keywords (NOT main categories - stored as tags)
const DIET_KEYWORDS = [
  'gluten-free', 'gluten free', 'dairy-free', 'dairy free', 'sugar-free', 'sugar free',
  'low-carb', 'low carb', 'keto', 'vegan', 'vegetarian', 'high-protein', 'high protein',
  'healthy', 'diabetic-friendly', 'diabetic friendly', ' Paleo', 'Whole30', ' Atkins'
];

// ============================================
// AUTO-SELECT CATEGORY FUNCTION - FIXED FOR WORDPRESS CATEGORIES
// ============================================
const autoSelectCategory = async (idx, articleData, categoriesOverride) => {
  const keyword = (articleData?.focusKeyword || '').toLowerCase();
  const title = (articleData?.title || '').toLowerCase();
  const combinedText = `${title} ${keyword}`;

  // Use override (from bulk action) > shared categories from parent > local categories
  const categoriesToUse = (categoriesOverride && categoriesOverride.length > 0)
    ? categoriesOverride
    : (sharedCategories && sharedCategories.length > 0 ? sharedCategories : categories);

  // If no categories loaded yet, wait for them
  if (!categoriesToUse || categoriesToUse.length === 0) {
    console.log('Categories not loaded yet');
    return;
  }

  // Extract diet keywords from content for tagging later
  const foundDietTags = DIET_KEYWORDS.filter(tag => combinedText.includes(tag.toLowerCase()));
  if (foundDietTags.length > 0) {
    onUpdate(idx, { dietTags: foundDietTags }, false);
  }

  // Category keyword mappings - maps content keywords to category name patterns
  const CATEGORY_KEYWORDS = {
    dessert: ['cake', 'cakes', 'cookie', 'cookies', 'brownie', 'brownies', 'cupcake', 'cupcakes', 'cheesecake', 'tart', 'pie', 'ice cream', 'pudding', 'mousse', 'dessert', 'sweet', 'treat', 'fudge', 'trifle', 'doughnut', 'donut', 'macaron', 'brittle', 'chocolate', 'candy', 'pastry', 'custard', 'bars', 'no-bake'],
    breakfast: ['pancake', 'pancakes', 'waffle', 'waffles', 'crepe', 'crepes', 'oatmeal', 'porridge', 'granola', 'muesli', 'cereal', 'eggs', 'benedict', 'hash', 'breakfast', 'smoothie bowl', 'acai', 'yogurt', 'parfait', 'muffin', 'scramble', 'frittata', 'omelette', 'quiche', 'biscuits', 'gravy'],
    lunch: ['rice bowl', 'pasta dish', 'chicken breast', 'beef', 'fish', 'salmon', 'shrimp', 'one-pan', 'one pan', 'meal-prep', 'meal prep', 'high-protein', 'protein', 'wrap', 'burrito', 'sandwich', 'submarine', 'kebab', 'skewer', 'grain bowl', 'power bowl', ' Cobb', 'Caesar', 'club sandwich', 'panini', 'croque', 'quesadilla', 'nachos', 'taco', 'tacos', 'empanada', 'calzone', 'pita', 'lunch'],
    dinner: ['soup', 'soups', 'stew', 'stews', 'chowder', 'gumbo', 'minestrone', 'broth', 'consomme', 'bisque', 'velloute', 'curry', 'casserole', 'casseroles', 'comfort food', 'family meal', 'roast', 'roasted', 'baked', 'braised', 'braise', 'simmered', 'poached', 'grilled', 'bbq', 'barbecue', 'stir-fry', 'stir fry', 'fried rice', 'noodles', 'lasagna', 'bolognese', 'alfredo', 'carbonara', 'risotto', 'paella', 'jambalaya', 'chili', 'hungry', 'filling', 'hearty', 'substantial'],
    snacks: ['energy ball', 'snack bar', 'granola bar', 'protein bar', 'quick bite', 'finger food', 'crackers', 'chips', 'pretzel', 'pretzels', 'popcorn', 'nuts', 'trail mix', 'mix', 'hummus', 'veggie sticks', 'salsa', 'guacamole', 'dip', 'dips', 'cheese', 'charcuterie', 'appetizer', 'starter'],
    drinks: ['juice', 'juices', 'smoothie', 'smoothies', 'shake', 'shakes', 'detox', 'lemonade', 'iced tea', 'coffee', 'espresso', 'latte', 'cappuccino', 'mocha', 'hot chocolate', 'cocktail', 'mocktail', 'beverage', 'water', 'infused', 'slushie', 'slushies', 'frappe', 'milkshake', 'ade'],
    bread: ['bread', 'rolls', 'buns', 'flatbread', 'naan', 'pita', 'focaccia', 'ciabatta', 'baguette', 'sourdough', 'yeast', 'fermented', 'homemade bakery', 'breadsticks', 'pizza dough', 'dough'],
    salads: ['salad', 'salads', 'greens', 'garden salad', 'caesar', 'greek', 'waldorf', 'potato salad', 'macaroni salad', 'pasta salad', 'rice salad', 'quinoa salad', 'couscous', 'tabbouleh', 'coleslaw', 'leafy', 'vegetable salad', 'protein salad', 'chicken salad', 'tuna salad', 'egg salad', 'seafood salad', 'wedge'],
    appetizers: ['appetizer', 'starter', 'bruschetta', 'caprese', 'dip', 'dips', 'fondue', 'mozzarella', 'jalapeno', 'poppers', 'spring roll', 'egg roll', 'wings', 'nachos', ' Loaded', 'party', 'celebration', 'reception', 'cocktail party', 'finger food', 'canape', 'canapes', 'sliders', 'mini', 'bites'],
    sauces: ['sauce', 'sauces', 'dressing', 'vinaigrette', 'mayonnaise', 'aioli', 'pesto', 'chimichurri', 'marinade', 'rub', 'glaze', 'gravy', 'salsa', 'chutney', 'relish', 'jam', 'jelly', 'butter', 'compound butter', 'spread', 'condiment', 'dip mix', 'seasoning', 'spice blend']
  };

  // Calculate scores for each WordPress category
  const categoryScores = categoriesToUse.map(cat => {
    const catName = (cat?.name || '').toLowerCase();
    let score = 0;

    // Check which keyword group this category belongs to
    Object.entries(CATEGORY_KEYWORDS).forEach(([groupName, keywords]) => {
      // Check if category name matches the group
      if (catName.includes(groupName) || keywords.some(kw => catName.includes(kw))) {
        keywords.forEach(kw => {
          // Exact word match (higher score)
          const exactRegex = new RegExp(`\\b${kw}\\b`, 'i');
          if (exactRegex.test(combinedText)) {
            score += 3;
          }
          // Partial match (lower score)
          else if (combinedText.includes(kw)) {
            score += 1;
          }
        });
      }
    });

    // Bonus: Direct category name match in content
    if (combinedText.includes(catName)) {
      score += 5;
    }

    return {
      id: cat?.id,
      name: cat?.name,
      score
    };
  });

  // Sort by score descending
  categoryScores.sort((a, b) => b.score - a.score);

  // Get the best match
  const bestMatch = categoryScores[0];

  // If we have a match with positive score, use it
  if (bestMatch && bestMatch.score > 0) {
    console.log(`Auto-categorized: "${articleData?.title}" → ${bestMatch.name} (score: ${bestMatch.score})`);
    setSelectedCategory(bestMatch.id);
    onUpdate(idx, { selectedCategory: bestMatch.id }, false);
    return;
  }

  // Fallback: Use first category if available
  if (categoriesToUse.length > 0) {
    const fallback = categoriesToUse[0];
    console.log(`Using fallback category: ${fallback.name}`);
    setSelectedCategory(fallback.id);
    onUpdate(idx, { selectedCategory: fallback.id }, false);
    return;
  }

  // No categories available
  console.log('No categories available for auto-categorization');
};




const handleSendToMidjourney = async () => {
  const prompt = mjPrompt?.generatedPrompt?.trim();
  if (!prompt) {
    alert('Generate a Midjourney prompt first');
    return;
  }
  if (mjSendState === 'sending' || mjSendState === 'processing' || mjSendState === 'upscaling') return;

  // 7s cooldown between sends
  const now = Date.now();
  const elapsed = now - mjLastSentRef.current;
  if (elapsed < 7000) {
    alert(`Please wait ${Math.ceil((7000 - elapsed) / 1000)}s before sending again.`);
    return;
  }
  mjLastSentRef.current = now;

  // Bind this request to THIS article's stable identity, not its array index —
  // if the article is deleted before Discord responds, the index could later
  // point at a different (shifted-into-place) article, and the index-based
  // onUpdate below must never be used to apply a late result.
  const requestArticleUid = article?._uid;

  // Clear any previous poll, then generate a fresh unique seed for THIS article.
  // The seed is embedded in the prompt so every Discord message is uniquely identifiable
  // and each article's status poller can only match its own message.
  if (mjPollRef.current) clearInterval(mjPollRef.current);
  const clientSeed = Math.floor(Math.random() * 4294967294) + 1;
  mjSeedRef.current = clientSeed;

  onMJBusyChange?.(true);
  setMJSendState('sending');
  setMJProgress(0);
  setMJResultUrl('');

  try {
    // ── Step 1: Send /imagine via Edge Function ───────────────────────
    const { data: { session } } = await supabase.auth.getSession();
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`,
    };
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

    // Unique identifier for this article's image batch — prevents images from
    // one article being assigned to another when multiple articles generate in parallel
    const batchId = crypto.randomUUID().replace(/-/g, '').slice(0, 8);
    mjBatchIdRef.current = batchId;

    // Strip any pre-existing --seed, then append our client-generated seed.
    // This guarantees the Discord message contains "--seed N" even if the server
    // edge function is the old version that doesn't add one itself.
    const promptWithSeed = prompt.replace(/--seed\s+\d+/gi, '').trim() + ` --seed ${clientSeed}`;

    const imagineRes = await fetch(`${SUPABASE_URL}/functions/v1/midjourney-imagine`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt: promptWithSeed, batchId }),
    });

    const imagineData = await imagineRes.json();
    if (!imagineRes.ok || !imagineData.success) {
      throw new Error(imagineData.error || 'Failed to send to Midjourney');
    }
    // Store the seed the server appended to this article's prompt.
    // The status poller uses it to match ONLY this article's Discord message.
    if (imagineData.seed) mjSeedRef.current = imagineData.seed;
    if (imagineData.batchId) mjBatchIdRef.current = imagineData.batchId;

    setMJSendState('processing');

    // ── Step 2: Poll midjourney-status every 5s ───────────────────────
    let pollCount = 0;
    const MAX_POLLS = 60; // 5 min max

    mjPollRef.current = setInterval(async () => {
      pollCount++;
      if (pollCount > MAX_POLLS) {
        clearInterval(mjPollRef.current);
        onMJBusyChange?.(false);
        setMJSendState('error');
        return;
      }

      try {
        const statusRes = await fetch(`${SUPABASE_URL}/functions/v1/midjourney-status`, {
          method: 'POST',
          headers,
          // Pass seed for new status function (matches by seedTag).
          // Pass "--seed N" as prompt so old status function also matches by the unique seed text.
          body: JSON.stringify({ seed: mjSeedRef.current, prompt: `--seed ${mjSeedRef.current}` }),
        });
        const statusData = await statusRes.json();

        if (statusData.status === 'done') {
          clearInterval(mjPollRef.current);
          setMJProgress(100);
          const gridUrl = statusData.imageUrl || '';
          const gridMessageId = statusData.messageId;
          const upscaleCustomIds = statusData.upscaleCustomIds || [];
          setMJResultUrl(gridUrl);

            const fallbackToGrid = async () => {
              // Download & store each image to permanent Hostinger storage
              const storeUrl = async (url) => {
                try {
                  const stored = await downloadAndStoreFromUrl(url);
                  return stored || url;
                } catch (_) { return url; }
              };
              const [storedMain, storedTop, storedMiddle, storedEnd] = await Promise.all([
                storeUrl(gridUrl),
                storeUrl(gridUrl),
                storeUrl(gridUrl),
                storeUrl(gridUrl),
              ]);
              setImagePreviews({ main: storedMain, top: storedTop, middle: storedMiddle, end: storedEnd });
              onApplyMJResult?.(requestArticleUid, {
                image_main_url: storedMain,
                image_top_url: storedTop,
                image_middle_url: storedMiddle,
                image_end_url: storedEnd,
              });
              onMJBusyChange?.(false);
              setMJSendState('done');
            };

          if (upscaleCustomIds.length === 4 && gridMessageId) {
            // Trigger all 4 upscales, then poll for individual CDN URLs
            setMJSendState('upscaling');
            const triggerUpscales = async () => {
              // Click U buttons sequentially with 9s delay to avoid Discord rate limits
              for (const customId of upscaleCustomIds) {
                await fetch(`${SUPABASE_URL}/functions/v1/midjourney-upscale`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({ messageId: gridMessageId, customId }),
                });
                await new Promise((r) => setTimeout(r, 1500));
              }

              // Poll for the 4 individual upscaled image URLs
              let upscalePollCount = 0;
              const MAX_UPSCALE_POLLS = 48; // ~4 min
              mjPollRef.current = setInterval(async () => {
                upscalePollCount++;
                if (upscalePollCount > MAX_UPSCALE_POLLS) {
                  clearInterval(mjPollRef.current);
                  fallbackToGrid();
                  return;
                }
                try {
                  const res = await fetch(`${SUPABASE_URL}/functions/v1/midjourney-upscale-status`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ messageId: gridMessageId }),
                  });
                  const data = await res.json();
                  if (data.status === 'done') {
                    clearInterval(mjPollRef.current);
                                  const [rawMain, rawTop, rawMiddle, rawEnd] = data.imageUrls;
                            // Download & store each upscaled image to permanent Hostinger storage
                            const storeUpscale = async (url) => {
                              try { return (await downloadAndStoreFromUrl(url)) || url; } catch (_) { return url; }
                            };
                            const [storedMain, storedTop, storedMiddle, storedEnd] = await Promise.all([
                              storeUpscale(rawMain),
                              storeUpscale(rawTop),
                              storeUpscale(rawMiddle),
                              storeUpscale(rawEnd),
                            ]);
                            setImagePreviews({ main: storedMain, top: storedTop, middle: storedMiddle, end: storedEnd });
                            onApplyMJResult?.(requestArticleUid, {
                              image_main_url: storedMain,
                              image_top_url: storedTop,
                              image_middle_url: storedMiddle,
                              image_end_url: storedEnd,
                            });
                            onMJBusyChange?.(false);
                            setMJSendState('done');
                          }
                } catch (_) { /* ignore poll errors */ }
              }, 5000);
            };
            triggerUpscales();
          } else {
            fallbackToGrid();
          }
        } else if (statusData.status === 'error') {
          clearInterval(mjPollRef.current);
          onMJBusyChange?.(false);
          setMJSendState('error');
        } else if (statusData.status === 'processing') {
          setMJProgress(statusData.progress || 0);
        }
      } catch (_) { /* ignore poll errors */ }
    }, 5000);

  } catch (err) {
    console.error('Midjourney send error:', err);
    onMJBusyChange?.(false);
    setMJSendState('error');
    alert(err?.message || 'Failed to send to Midjourney');
  }
};

const handleGenerateMJPrompt = async () => {
  if (!article?.focusKeyword?.trim() || !mjPrompt?.referenceImage) {
    alert('Focus keyword and reference image are required');
    return;
  }

  if (isGeneratingPrompt) return;
  setIsGeneratingPrompt(true);

  try {
    // 1️⃣ Get template from tools/prompts - FIXED: check promptType (camelCase)
    const template =
      prompts?.find(
        (p) =>
          p?.name?.toLowerCase() === 'image description' ||
          p?.promptType === 'image description'
      )?.content;

    if (!template) {
      throw new Error('Image Description prompt template not found');
    }

    // 2️⃣ Fill placeholders
    const finalPrompt = template?.replaceAll('{{imageUrl}}', mjPrompt?.referenceImage?.trim())?.replaceAll('{{focusKeyword}}', article?.focusKeyword?.trim())?.replaceAll('{{aspectRatio}}', mjPrompt?.aspectRatio);


    // 3️⃣ Show prompt in UI
    setMJPrompt((prev) => ({
      ...prev,
      generatedPrompt: finalPrompt
    }));

    // 4️⃣ Save for bulk / persistence
    onUpdate(index, { mjPrompt: finalPrompt }, false);

  } catch (error) {
    console.error('MJ prompt generation failed:', error);
    alert(error?.message || 'Midjourney prompt failed');
  } finally {
    setIsGeneratingPrompt(false);
  }
};





// Title suggestions (simple: pick first suggestion)
const handleGenerateTitleSuggestions = async () => {
  if (!article?.focusKeyword?.trim()) return;

  setIsGeneratingTitles(true);
  try {
    const baseTitle = article?.title || article?.shortDescription || article?.focusKeyword;
    const titles = await generateTitlesFromEdge(article?.focusKeyword, selectedLanguage);
    const selectedTitle = pickBestTitle(titles, article?.focusKeyword, selectedLanguage);
    const finalTitle = selectedTitle || buildSEOTitle(baseTitle, article?.focusKeyword, selectedLanguage);
    onUpdate(index, { title: finalTitle }, false);
  } catch (error) {
    console.error('Error generating title:', error);
  } finally {
    setIsGeneratingTitles(false);
  }
};

// Focus keyword (single primary keyword)
const handleGenerateFocusKeyword = async () => {
  const baseTitle = article?.title || article?.shortDescription || article?.focusKeyword;
  if (!baseTitle?.trim()) return;

  setIsGeneratingKeywords(true);
  try {
    let keyword = '';
    try {
      keyword = await generateFocusKeywordFromEdge(baseTitle);
    } catch (err) {
      console.warn('Focus keyword edge function failed, using fallback:', err);
    }
    const finalKeyword = keyword || deriveFocusKeywordFromText(baseTitle);
    if (finalKeyword) onUpdate(index, { focusKeyword: finalKeyword }, false);
  } catch (error) {
    console.error('Error generating focus keyword:', error);
  } finally {
    setIsGeneratingKeywords(false);
  }
};

const handleGenerateMetaDescription = async () => {
  if (!article?.focusKeyword?.trim() || !article?.title?.trim()) return;

  try {
    let metaDescription = '';
    try {
      metaDescription = await generateMetaDescriptionFromEdge({
        title: article?.title,
        focusKeyword: article?.focusKeyword,
        shortDescription: article?.shortDescription,
        language: selectedLanguage
      });
    } catch (err) {
      console.warn('Meta description edge function failed, using fallback:', err);
    }
    const finalMeta = metaDescription || generateMetaDescriptionLocal(article?.title, article?.focusKeyword, selectedLanguage);
    onUpdate(index, { metaDescription: finalMeta }, false);
  } catch (error) {
    console.error('Error generating meta description:', error);
  }
};

  const handleGenerateSlug = async () => {
  if (!article?.focusKeyword?.trim()) return;

  const userId = await getUserId();
  const slug = await generateSlugForUser(
    article?.focusKeyword,
    existingSlugs,
    userId
  );
  if (slug) onUpdate(index, { permalink: slug }, false);
};

// ✅ THEN expose them (for global buttons)
useImperativeHandle(ref, () => ({
  autoSelectCategory,
  setSelectedCategory,
  generateMJPrompt: handleGenerateMJPrompt,
  sendToMidjourney: handleSendToMidjourney,
  generateTitle: handleGenerateTitleSuggestions,
  generateKeywords: handleGenerateFocusKeyword,
  generate10FocusKeywords: handleGenerate10FocusKeywords
}));

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState(article?.referenceImageUrl || '');

  // NEW: State for title and keyword suggestions
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [isGeneratingTitles, setIsGeneratingTitles] = useState(false);
  const [keywordSuggestions, setKeywordSuggestions] = useState('');
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);

  // Load categories when site is selected
  useEffect(() => {
    if (selectedSite) {
      loadCategories();
      // Preload categories in background for faster subsequent access
      wordpressService?.preloadCategories(selectedSite);
    }
  }, [selectedSite]);

  const loadCategories = async () => {
    if (!selectedSite) return;

    setLoadingCategories(true);
    try {
      // Fetch categories with automatic token refresh and caching
      const result = await fetchWordPressCategories(selectedSite);
      
      if (result?.success) {
        setCategories(result?.categories || []);
        setCategoryError(null);
      } else {
        setCategories([]);
        setCategoryError(result?.error || 'Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
      setCategoryError(error?.message || 'Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

// FIXED: Do NOT restore old reference image if reset all created a blank article
useEffect(() => {
  if (!article?.referenceImageUrl) {
    // clear everything when empty
    setMJPrompt((prev) => ({ ...prev, referenceImage: '' }));
    setReferenceImagePreview('');
    return;
  }

  // only apply for imported or pre-filled articles
  setMJPrompt((prev) => ({
    ...prev,
    referenceImage: article?.referenceImageUrl
  }));
  setReferenceImagePreview(article?.referenceImageUrl);
}, [article?.referenceImageUrl]);

  // NEW: Retry categories function
  const handleRetryCategories = async () => {
    const siteToUse = article?.siteId || selectedSite;

    if (!siteToUse) {
      alert('Please select a site first');
      return;
    }

    setLoadingCategories(true);
    setCategoryError(null);

    try {
      const result = await fetchWordPressCategories(siteToUse);

      if (result?.success) {
        setCategories(result?.categories || []);
      } else {
        setCategoryError(result?.error || 'Failed to load categories');
      }
    } catch (err) {
      setCategoryError(err?.message || 'Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  // NEW: Handle title suggestion click
  const handleSelectTitleSuggestion = (suggestion) => {
    onUpdate(index, { title: suggestion });
    setTitleSuggestions([]); // Clear suggestions after selection
  };

  const handleImageUpload = async (location, file) => {
    try {
      // ✅ STEP 1: Validate original file
      imageService?.validateImageFile(file);

      // ✅ STEP 2: Convert to WebP and remove metadata (iPhone 16 Pro Max assumption)
      console.log(`🔄 Processing ${location} image: Converting to WebP and removing metadata...`);
      const processedFile = await imageService?.convertToWebP(file, 0.85);
      
      if (!processedFile) {
        throw new Error('Image processing failed');
      }

      console.log(`✅ Image processed: ${(file?.size / 1024)?.toFixed(2)}KB → ${(processedFile?.size / 1024)?.toFixed(2)}KB`);

      // ✅ STEP 3: Upload processed WebP image to Supabase Storage
      const url = await imageService?.uploadToStorage(processedFile, { folder: 'article_images' });

      // ✅ STEP 4: Save URL to article state
      onUpdate(index, { [`image_${location}_url`]: url });

      // ✅ STEP 5: Update local file state
      setImages((prev) => ({
        ...prev,
        [location]: processedFile
      }));

      // ✅ STEP 6: Create preview
      const previewUrl = URL.createObjectURL(processedFile);
      setImagePreviews((prev) => ({
        ...prev,
        [location]: previewUrl
      }));

      console.log(`✅ ${location} image uploaded successfully (WebP, metadata removed)`);
    } catch (error) {
      console.error('Image upload error:', error);
      alert(error?.message || 'Failed to upload image');
    }
  };

  // Swap two image slots (preview, file object, and stored URL)
  const handleImageSwap = (fromLoc, toLoc) => {
    if (!fromLoc || !toLoc || fromLoc === toLoc) return;
    setImages((prev) => {
      const next = { ...prev };
      [next[fromLoc], next[toLoc]] = [next[toLoc], next[fromLoc]];
      return next;
    });
    setImagePreviews((prev) => {
      const next = { ...prev };
      [next[fromLoc], next[toLoc]] = [next[toLoc], next[fromLoc]];
      return next;
    });
    const fromUrl = article?.[`image_${fromLoc}_url`] ?? null;
    const toUrl   = article?.[`image_${toLoc}_url`]   ?? null;
    onUpdate(index, {
      [`image_${fromLoc}_url`]: toUrl,
      [`image_${toLoc}_url`]:   fromUrl,
    });
  };

  // NEW: Bulk image upload handler (1-4 images)
  const handleBulkImageUpload = async (files) => {
    if (!files || files?.length === 0 || files?.length > 4) {
      alert('Please select 1-4 images');
      return;
    }

    const fileArray = Array.from(files);
    const locations = ['main', 'top', 'middle', 'end'];

    try {
      for (let i = 0; i < fileArray?.length; i++) {
        const file = fileArray?.[i];
        const location = locations?.[i];
        
        await handleImageUpload(location, file);
      }

    } catch (error) {
      console.error('Bulk image upload error:', error);
      alert('Failed to upload some images. Please try again.');
    }
  };



  const copyMJPrompt = async () => {
    // CRITICAL CLEANUP: Clean prompt to remove ANY labels, quotes, or formatting
    const cleanPrompt = mjPrompt?.generatedPrompt?.
    replace(/^["']|["']$/g, '') // Remove surrounding quotes
    ?.replace(/^MidJourney Prompt:\s*/i, '') // Remove MidJourney Prompt: prefix
    ?.replace(/^Prompt:\s*/i, '') // Remove generic Prompt: prefix
    ?.replace(/\*\*/g, '') // Remove markdown bold
    ?.replace(/\*/g, '') // Remove markdown italic
    ?.trim();

    // Copy to clipboard
    await navigator.clipboard?.writeText(cleanPrompt);

    // Show success state - button disabled to prevent multiple clicks
    setIsCopied(true);

    // Revert to normal state after 2 seconds
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  // Removed auto SEO title generation to avoid connection errors

  return (
    <div className="rounded-[17px] shadow-sm p-4 md:p-6 mb-8 bg-[rgba(184, 219, 71, 1)] border-[3px] border-[rgba(135,190,25,1)]">
      <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Article {index + 1}
          </h3>
          {showPorkAlcoholWarning &&
            <span className="font-bold text-red-600">
              ⚠️ THIS RECIPE CONTAINS PORK AND/OR ALCOHOL
            </span>
          }
        </div>
        {articlesLength > 1 &&
        <button
          onClick={() => onDelete(index)}
          className="p-2 text-red-600 hover:bg-red-50 rounded-md"
          title="Delete article">

            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        }
      </div>
      {/* Article Details - 2 Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* LEFT: Article Title, Slug, Category, Recipe Card */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Article Title *
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  value={article?.title || ''}
                  onChange={(e) => onUpdate(index, { title: e?.target?.value })}
                  placeholder="Auto-generated or enter manually"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
              </div>
              <button
                onClick={handleGenerateTitleSuggestions}
                disabled={!article?.focusKeyword || isGeneratingTitles}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-yellow-300 bg-yellow-50 text-yellow-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Generate title suggestions">
                {isGeneratingTitles ?
                  <Loader2 className="w-5 h-5 animate-spin" /> :
                  <Lightbulb className="w-5 h-5" />
                }
              </button>
            </div>
            {titleSuggestions?.length > 0 &&
              <div className="mt-1 space-y-1">
                {titleSuggestions?.map((suggestion, idx) =>
                  <button
                    key={idx}
                    onClick={() => handleSelectTitleSuggestion(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 transition-colors">
                    {suggestion}
                  </button>
                )}
              </div>
            }
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  value={article?.permalink || ''}
                  onChange={(e) => onUpdate(index, { permalink: e?.target?.value })}
                  placeholder="e.g., chocolate-chip-cookies_1"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
              </div>
              <button
                onClick={handleGenerateSlug}
                disabled={!article?.focusKeyword}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-yellow-300 bg-yellow-50 text-yellow-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Generate slug">
                <Lightbulb className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  value={selectedCategory !== null ? String(selectedCategory) : ''}
                  onChange={(value) => {
                    // Handle both string and numeric IDs
                    const id = isNaN(parseInt(value, 10)) ? value : parseInt(value, 10);
                    setSelectedCategory(id);
                    onUpdate(index, { selectedCategory: id });
                  }}
                  options={sharedCategories?.map((cat) => ({
                    value: cat?.id?.toString(),
                    label: cat?.name
                  }))}
                  loading={sharedCategoriesLoading && (!sharedCategories || sharedCategories?.length === 0)}
                  placeholder={sharedCategoriesLoading && (!sharedCategories || sharedCategories?.length === 0) ? 'Loading categories...' : 'Select a category'}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
              </div>
              <button
                type="button"
                onClick={() => autoSelectCategory(index, article)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-yellow-300 bg-yellow-50 text-yellow-600 hover:bg-yellow-100"
                title="Suggest category based on focus keyword">
                <Lightbulb className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`recipe-card-${index}`}
              checked={article?.enableRecipeCard || false}
              onChange={(e) => onUpdate(index, { enableRecipeCard: e?.target?.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor={`recipe-card-${index}`}
              className="text-sm font-medium text-gray-700"
            >
              Enable Tasty Recipe Card
            </label>
          </div>
        </div>

        {/* RIGHT: Focused Keyword + Meta Description */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Focused Keyword *
            </label>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <textarea
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="3"
                  value={article?.focusKeyword || ''}
                  onChange={(e) => onUpdate(index, { focusKeyword: e?.target?.value })}
                  placeholder="e.g., Chocolate Chip Cookies"
                />
              </div>
              <button
                onClick={handleGenerateFocusKeyword}
                disabled={(!article?.title && !article?.shortDescription && !article?.focusKeyword) || isGeneratingKeywords}
                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-md border border-yellow-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Generate focused keyword">
                {isGeneratingKeywords ?
                  <Loader2 className="w-5 h-5 animate-spin" /> :
                  <Lightbulb className="w-5 h-5" />
                }
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meta Description
            </label>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <textarea
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-y-auto max-h-40"
                  rows="4"
                  value={article?.metaDescription || ''}
                  onChange={(e) => onUpdate(index, { metaDescription: e?.target?.value })}
                  placeholder="Brief summary for search results"
                />
              </div>
              <button
                onClick={handleGenerateMetaDescription}
                disabled={!article?.focusKeyword || !article?.title}
                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-md border border-yellow-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Generate meta description">
                <Lightbulb className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Category Error Messages */}
      {categoryError &&
      <div className="mb-4">
          <div className="px-3 py-2 border border-red-300 rounded-md bg-red-50">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">{categoryError}</p>
                
                {/* Authentication Error - Show credential fix */}
                {categoryError?.includes('Authentication Failed') &&
              <div className="ml-7 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <p className="font-semibold text-yellow-800 mb-1">💡 How to fix:</p>
                    <ol className="text-yellow-700 space-y-1 ml-4 list-decimal">
                      <li>Go to <strong>WordPress → Sites</strong> page</li>
                      <li>Edit the site and verify <strong>Application Password</strong> is correct</li>
                      <li>Regenerate Application Password in WordPress if needed</li>
                      <li>Ensure username matches WordPress admin username</li>
                    </ol>
                  </div>
              }
                
                {/* REST API Disabled Error */}
                {categoryError?.includes('REST API') &&
              <div className="ml-7 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <p className="font-semibold text-yellow-800 mb-1">💡 How to fix:</p>
                    <ol className="text-yellow-700 space-y-1 ml-4 list-decimal">
                      <li>Contact WordPress site administrator</li>
                      <li>Ensure <strong>WordPress REST API is enabled</strong></li>
                      <li>Verify no security plugins are blocking REST API</li>
                      <li>Check .htaccess file for REST API restrictions</li>
                    </ol>
                  </div>
              }
                
                {/* Network Error - Show connectivity troubleshooting */}
                {categoryError?.includes('Cannot reach') &&
              <div className="ml-7 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <p className="font-semibold text-yellow-800 mb-1">💡 How to fix:</p>
                    <ol className="text-yellow-700 space-y-1 ml-4 list-decimal">
                      <li>Verify <strong>Site URL is correct</strong> in Sites settings</li>
                      <li>Check if WordPress site is online (try opening in browser)</li>
                      <li>Ensure site URL uses <strong>https://</strong> (not http://)</li>
                      <li>Check if site is behind firewall or IP restrictions</li>
                    </ol>
                  </div>
              }
                
                {/* CORS/Extension Error - Show existing troubleshooting */}
                {(categoryError?.includes('CORS') || categoryError?.includes('Extension')) &&
              <div className="ml-7 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <p className="font-semibold text-yellow-800 mb-1">💡 How to fix:</p>
                    <ol className="text-yellow-700 space-y-1 ml-4 list-decimal">
                      <li><strong>Disable browser extensions</strong> (especially Frame Ant, ad blockers)</li>
                      <li>Open browser in <strong>Incognito/Private mode</strong></li>
                      <li>Install <strong>"WP REST API CORS"</strong> plugin on WordPress</li>
                      <li>Or add to wp-config.php:<br />
                        <code className="bg-white px-1 text-xs">header('Access-Control-Allow-Origin: *');</code>
                      </li>
                    </ol>
                  </div>
              }
                
                {/* Timeout Error - Show performance troubleshooting */}
                {categoryError?.includes('timeout') &&
              <div className="ml-7 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <p className="font-semibold text-yellow-800 mb-1">💡 How to fix:</p>
                    <ol className="text-yellow-700 space-y-1 ml-4 list-decimal">
                      <li>Check if WordPress site is responding slowly</li>
                      <li>Verify <strong>hosting server is online</strong> and responding</li>
                      <li>Check WordPress site for performance issues</li>
                      <li>Try again when site load is lower</li>
                    </ol>
                  </div>
              }
                
                <div className="ml-7 mt-3">
                  <button
                  onClick={handleRetryCategories}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-[#FFD32C] hover:bg-[#FFD32C]/90 rounded-md border border-red-300 transition-colors">

                    🔄 Retry fetching categories
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      {/* Images Section - 2 Equal Columns */}
      <div className="border-t border-gray-200 pt-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

          {/* ── LEFT COLUMN: Uploaded Images ── */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Uploaded Images</h4>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = e?.target?.files;
                    if (files && files?.length > 0) handleBulkImageUpload(files);
                  }}
                  className="hidden"
                  id={`bulk-upload-${index}`}
                />
                <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors cursor-pointer">
                  <ImageIcon className="w-3 h-3 mr-1.5" />
                  4 img in row
                </span>
              </label>
            </div>

            {/* Image rows */}
            <div className="space-y-2 flex-1">
              {[
                { location: 'main',   label: 'Main'   },
                { location: 'top',    label: 'Top'    },
                { location: 'middle', label: 'Middle' },
                { location: 'end',    label: 'End'    },
              ].map((imageRow) => (
                <div
                  key={imageRow.location}
                  draggable={!!imagePreviews?.[imageRow.location]}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    setDragFrom(imageRow.location);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragFrom && dragFrom !== imageRow.location) setDragOver(imageRow.location);
                  }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragFrom && dragFrom !== imageRow.location) handleImageSwap(dragFrom, imageRow.location);
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                  className={`flex items-center gap-3 px-3 py-2.5 bg-white rounded-xl border shadow-sm transition-all duration-150 ${
                    dragOver === imageRow.location && dragFrom !== imageRow.location
                      ? 'border-blue-400 bg-blue-50 shadow-blue-100 scale-[1.01]'
                      : dragFrom === imageRow.location
                      ? 'border-gray-300 opacity-50'
                      : 'border-gray-200'
                  } ${imagePreviews?.[imageRow.location] ? 'cursor-grab active:cursor-grabbing' : ''}`}
                >
                  <span className="text-sm font-semibold text-gray-500 w-14 flex-shrink-0">
                    {imageRow.label}
                    {imageRow.location === 'main' && <span className="text-red-500 ml-0.5">*</span>}
                  </span>

                  {imagePreviews?.[imageRow.location] ? (
                    <img
                      src={imagePreviews[imageRow.location]}
                      alt={`${imageRow.label} preview`}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0 shadow-sm pointer-events-none"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-5 h-5 text-gray-300" />
                    </div>
                  )}

                  <div className="flex items-center gap-2 ml-auto">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e?.target?.files?.[0] && handleImageUpload(imageRow.location, e.target.files[0])}
                        className="hidden"
                        id={`upload-${imageRow.location}-${index}`}
                      />
                      <label
                        htmlFor={`upload-${imageRow.location}-${index}`}
                        className="cursor-pointer inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                      >
                        <ImageIcon className="w-3 h-3 mr-1" />
                        {images?.[imageRow.location] ? 'Change' : 'Upload'}
                      </label>
                    </label>
                    <button
                      onClick={() => {
                        setImages((prev) => ({ ...prev, [imageRow.location]: null }));
                        setImagePreviews((prev) => ({ ...prev, [imageRow.location]: null }));
                        onUpdate(index, {
                          [`image_${imageRow.location}`]: null,
                          [`image_${imageRow.location}_url`]: null,
                        });
                      }}
                      disabled={!images?.[imageRow.location] && !imagePreviews?.[imageRow.location]}
                      className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Delete"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-2">
              <p className="text-xs text-gray-400">
                Main Image is required. All images will be converted to WebP with AI-generated metadata.
              </p>
            </div>
          </div>

          {/* ── RIGHT COLUMN: AI Image Generation ── */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">AI Image Generation</h4>

            {/* Reference image URL + preview */}
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  value={mjPrompt?.referenceImage}
                  onChange={(e) => {
                    const newUrl = e?.target?.value;
                    setMJPrompt((prev) => ({ ...prev, referenceImage: newUrl }));
                    setReferenceImagePreview(newUrl);
                  }}
                  placeholder="https://example.com/reference-image.jpg"
                  className="text-sm"
                />
              </div>
              {referenceImagePreview && (
                <img
                  src={referenceImagePreview}
                  alt="Reference preview"
                  className="w-16 h-16 object-cover rounded-xl border border-gray-200 shadow-sm flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>

            {/* Image Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Size</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: 'Portrait',      value: '2:3'  },
                  { label: 'Square',        value: '1:1'  },
                  { label: 'Landscape',     value: '3:2'  },
                  { label: 'Vertical Reel', value: '9:16' },
                ].map((size) => (
                  <button
                    key={size.value}
                    onClick={() => setMJPrompt((prev) => ({ ...prev, aspectRatio: size.value }))}
                    className={`px-2 py-2 text-xs font-medium rounded-xl border transition-colors ${
                      mjPrompt?.aspectRatio === size.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt textarea + Send to Midjourney */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Description (Prompt)</label>
              <div className="border border-gray-200 rounded-2xl bg-white overflow-hidden shadow-sm">
                <textarea
                  className="w-full px-4 py-3 text-sm resize-none border-0 focus:ring-0 focus:outline-none bg-transparent"
                  rows="3"
                  value={mjPrompt?.generatedPrompt}
                  onChange={(e) => setMJPrompt((prev) => ({ ...prev, generatedPrompt: e?.target?.value }))}
                  placeholder="AI-generated prompt will appear here..."
                />
                <div className="flex flex-col sm:flex-row border-t border-gray-200">
                  <Button
                    onClick={handleGenerateMJPrompt}
                    disabled={!article?.focusKeyword || isGeneratingPrompt}
                    className="w-full sm:flex-1 h-11 rounded-none sm:rounded-bl-[20px] border-0 border-b sm:border-b-0 sm:border-r border-gray-200 bg-green-600 text-white text-sm hover:bg-green-700 justify-center">
                    {isGeneratingPrompt ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-1" />Generate</>
                    )}
                  </Button>
                  <Button
                    onClick={copyMJPrompt}
                    disabled={!mjPrompt?.generatedPrompt || isCopied}
                    variant="outline"
                    className={`w-full sm:flex-1 h-11 rounded-none border-0 border-b sm:border-b-0 sm:border-r border-gray-200 text-sm transition-all duration-200 ${
                      isCopied ? 'bg-green-500 text-white' : 'bg-white text-gray-700'
                    }`}>
                    {isCopied ? (
                      <><CheckCircle className="w-4 h-4 mr-1" />Copied!</>
                    ) : (
                      <><Copy className="w-4 h-4 mr-1" />Copy</>
                    )}
                  </Button>
                  <Button
                    onClick={handleSendToMidjourney}
                    disabled={!mjPrompt?.generatedPrompt || mjSendState === 'sending' || mjSendState === 'processing' || mjSendState === 'upscaling' || (mjGlobalBusy && mjSendState === 'idle')}
                    className="w-full sm:flex-1 h-11 rounded-none sm:rounded-br-[20px] border-0 bg-[#ddaf00] text-white text-sm hover:bg-[#d4a800] justify-center disabled:opacity-60">
                    {mjSendState === 'sending' ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending...</>
                    ) : mjSendState === 'processing' ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />{mjProgress > 0 ? `${mjProgress}%` : 'Processing...'}</>
                    ) : mjSendState === 'upscaling' ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Upscaling...</>
                    ) : (
                      <>🎨 Send to Midjourney</>
                    )}
                  </Button>
                </div>

                {mjSendState === 'done' && mjResultUrl && (
                  <div className="flex flex-col items-center gap-2 px-3 pb-3 pt-1">
                    <span className="text-xs text-green-700 font-medium">✅ Generated!</span>
                    <a
                      id="ztu8ok"
                      href={mjResultUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium shadow-sm hover:bg-blue-100 hover:border-blue-300 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Open Full Size
                    </a>
                  </div>
                )}
                {mjSendState === 'error' && (
                  <p className="px-3 pb-2 text-xs text-red-500">❌ Midjourney failed. Try again or check the Discord channel.</p>
                )}
              </div>
            </div>

            {/* Send just this article to the Blog Posts Queue */}
            <div className="flex justify-end pt-1">
              <button
                onClick={handleSendToQueue}
                disabled={isSendingToQueue}
                title="Send this article to the Blog Posts Queue"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-green-600 text-white text-xs font-medium shadow-sm hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {isSendingToQueue ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" />Sending...</>
                ) : (
                  <><ArrowRight className="w-3.5 h-3.5" />Send to Queue</>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>
      
      





      
      {/* Inline Success Message - NEW */}
      {article?.status === 'completed' && (
      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">
                Article sent to Blog Posts successfully!
              </p>
              {article?.wpPostUrl &&
            <a
              href={article?.wpPostUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-green-700 hover:underline">

                  View in WordPress →
                </a>
            }
            </div>
          </div>
        </div>
      )}
      {article?.status === 'error' && (
       <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
         <div className="flex items-center space-x-2">
           <AlertCircle className="w-5 h-5 text-red-600" />
           <p className="text-sm text-red-900">{article?.errorMessage}</p>
         </div>
       </div>
     )}
    </div>
  );
});


export default function AddBlogPost() {
  const location = useLocation();
  const navigate = useNavigate();
  const { alert } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startAddBlogSitesRequest = useRequestGuard();
  const startAddBlogPromptsRequest = useRequestGuard();

  const articleRefs = useRef([]);
  const articleDomRefs = useRef([]);

  const [sites, setSites] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [publishingStatus, setPublishingStatus] = useState('publish');
  const [openDropdown, setOpenDropdown] = useState(null);

  // The language dropdown is display-only, driven entirely by the active
  // language profile — requirement is that the profile's language controls
  // the complete generation, so it must never be able to disagree with it.
  const selectedLanguage = activeProfile?.language_code || 'en';

  const [bulkAction, setBulkAction] = useState(null);

  // Workflow gating: Titles & Keywords must run before Categorize, which must run before MJ
  const [titlesGenerated, setTitlesGenerated] = useState(false);
  const [categoriesGenerated, setCategoriesGenerated] = useState(false);

  // Global "Set Category for All" dropdown selection
  const [globalCategorySelection, setGlobalCategorySelection] = useState('');

  // Shared categories state for all article blocks
  const [sharedCategories, setSharedCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // CSV import
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = useRef(null);

  // Global MJ concurrency: max 10 cards generating at once
  const [mjActiveCount, setMjActiveCount] = useState(0);
  const handleMJBusyChange = (busy) =>
    setMjActiveCount((prev) => Math.max(0, prev + (busy ? 1 : -1)));

  // MJ queue management refs
  const mjQueueAbortRef = useRef(false);
  const mjCountdownTimerRef = useRef(null);
  const [mjCountdownModal, setMjCountdownModal] = useState(null); // null | { seconds: number }
  const articlesRef = useRef([]);
  // Tracks whether the lazy initializer already restored from localStorage,
  // so the mount effect can skip loadQueuedArticles and the in-effect restore.
  const restoredFromCache = useRef(false);

  const [articles, setArticles] = useState(() => {
    // Lazy initializer: runs synchronously during the first render so that
    // ArticleBlock mounts with image URLs already in the article prop and
    // imagePreviews initializes correctly from them.
    if (location?.state?.importedArticle) return [makeEmptyArticle()];
    try {
      const cached = JSON.parse(localStorage.getItem('addpost_articles_cache') || 'null');
      const hasRealData = Array.isArray(cached) && cached.length > 0 &&
        cached.some(a =>
          a?.image_main_url || a?.image_top_url || a?.image_middle_url || a?.image_end_url ||
          (!a?.isFromCopyBlogs && (a?.focusKeyword || a?.title))
        );
      if (hasRealData) {
        restoredFromCache.current = true;
        const restored = cached.map(a => ({ ...a, _uid: a._uid || crypto.randomUUID() }));
        articlesRef.current = restored;
        return restored;
      }
    } catch {}
    return [makeEmptyArticle()];
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const autosaveTimerRef = useRef(null);
  const hasUnsavedChanges = useRef(false);

  useEffect(() => {
    loadSites();
    loadPrompts();

    // Cache is already loaded by the useState lazy initializer above.
    // Only run loadQueuedArticles when no cache was found on this mount.
    if (!restoredFromCache.current) {
      loadQueuedArticles();
    }

    // Load articles queued from Copy Blogs (persistent — stays until sent/deleted/reset),
    // scoped to the currently active language profile only.
    if (!restoredFromCache.current) try {
      const pending = JSON.parse(localStorage.getItem(getCopyBlogsQueueKey(activeProfile?.id)) || '[]');
      if (pending.length > 0) {
        setArticles(pending.map(imported => mapCopyBlogsQueueEntry(imported, activeProfile?.id || null)));
        return; // skip loadQueuedArticles replacing these
      }
    } catch {}

    // Fallback: single article passed via navigation state
    if (location?.state?.importedArticle) {
      const imported = location?.state?.importedArticle;
      setArticles([{
        _uid: crypto.randomUUID(),
        id: null,
        focusKeyword: imported?.focusKeyword || '',
        title: imported?.title || '',
        shortDescription: imported?.shortDescription || '',
        referenceImageUrl: imported?.referenceImageUrl || '',
        enableRecipeCard: true,
        seoTitle: '',
        permalink: '',
        metaDescription: '',
        secondaryKeywords: [],
        tags: [],
        category: imported?.category || '',
        status: null,
        wpPostId: null,
        wpPostUrl: null,
        errorMessage: null,
        siteId: null,
        isFromCopyBlogs: true,
        sourceArticleUrl: imported?.sourceArticleUrl
      }]);
    }

    // Save to localStorage synchronously on page close/refresh so images are never lost.
    // articlesRef.current is always up-to-date (kept in sync by a separate useEffect).
    const handleBeforeUnload = () => {
      try {
        localStorage.setItem('addpost_articles_cache', JSON.stringify(articlesRef.current));
      } catch {}
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (autosaveTimerRef?.current) {
        clearTimeout(autosaveTimerRef?.current);
      }
      // Flush any pending debounce immediately on SPA navigation away
      try {
        localStorage.setItem('addpost_articles_cache', JSON.stringify(articlesRef.current));
      } catch {}
    };
  }, []);

  // Re-scope the site dropdown and any queued Copy Blogs articles when the
  // active language profile changes — deliberately separate from the mount
  // effect above so switching profiles never re-triggers the general
  // addpost_articles_cache/navigation-state restoration, only the
  // profile-scoped pieces.
  const isFirstProfileLoad = useRef(true);
  useEffect(() => {
    if (isFirstProfileLoad.current) {
      isFirstProfileLoad.current = false;
      return;
    }
    setSelectedSite('');
    setSelectedPrompt('');
    // Clear the previous profile's dropdown options immediately so the
    // site/prompt pickers can't briefly show another profile's data while
    // the new profile's lists are loading.
    setSites([]);
    setPrompts([]);
    loadSites();
    loadPrompts();

    // Drop any still-queued (untouched, unsent) Copy Blogs articles that
    // belonged to the previous profile, and pull in the newly active
    // profile's own queue — same isolation the mount effect already
    // applies, re-run here so a live in-page switch gets it too. Articles
    // the user is actively editing (manual entries, or Copy Blogs imports
    // already sent/in progress) are left alone.
    const newProfileId = activeProfile?.id || null;
    setArticles((prev) => {
      const kept = prev.filter(
        (a) => !(a?.isFromCopyBlogs && !a?.status && a?.profileId !== newProfileId)
      );

      let incoming = [];
      try {
        const raw = JSON.parse(localStorage.getItem(getCopyBlogsQueueKey(newProfileId)) || '[]');
        incoming = raw.map((imported) => mapCopyBlogsQueueEntry(imported, newProfileId));
      } catch {}

      if (incoming.length === 0) {
        return kept.length > 0 ? kept : [makeEmptyArticle()];
      }

      const keptIsJustBlankDefault =
        kept.length === 1 && !kept[0]?.isFromCopyBlogs && !articleHasContent(kept[0]);

      return keptIsJustBlankDefault ? incoming : [...kept, ...incoming];
    });
  }, [activeProfile?.id]);

  // Load shared categories when site is selected
  useEffect(() => {
    if (selectedSite) {
      setSharedCategories([]); // clear stale categories from previous site immediately
      loadSharedCategories(selectedSite);
    }
  }, [selectedSite]);

  // Keep articlesRef in sync with articles state for async queue access
  useEffect(() => { articlesRef.current = articles; }, [articles]);

  // Persist articles (including image URLs) to localStorage so they survive refresh/reopen
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem('addpost_articles_cache', JSON.stringify(articles));
      } catch {}
    }, 500);
    return () => clearTimeout(timer);
  }, [articles]);

  // Countdown ticker for MJ auto-send modal
  useEffect(() => {
    if (mjCountdownModal === null) return;
    if (mjCountdownModal.seconds <= 0) {
      setMjCountdownModal(null);
      setBulkAction(null);
      sendAllToBlogPosts();
      return;
    }
    const t = setTimeout(() => {
      setMjCountdownModal(prev => prev ? { ...prev, seconds: prev.seconds - 1 } : null);
    }, 1000);
    mjCountdownTimerRef.current = t;
    return () => clearTimeout(t);
  }, [mjCountdownModal]);

  // Cleanup MJ queue on unmount
  useEffect(() => {
    return () => {
      mjQueueAbortRef.current = true;
      if (mjCountdownTimerRef.current) clearTimeout(mjCountdownTimerRef.current);
    };
  }, []);

  // Navigate to previous or next article section (used by floating nav buttons)
  const snapToArticle = useCallback((dir) => {
    const els = articleDomRefs.current.filter(Boolean);
    if (!els.length) return;

    const HEADER_OFFSET = 80;
    let bestIdx = 0;
    let bestDist = Infinity;
    els.forEach((el, i) => {
      const top = el.getBoundingClientRect().top - HEADER_OFFSET;
      const dist = Math.abs(top);
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    });

    const targetIdx = dir > 0
      ? Math.min(bestIdx + 1, els.length - 1)
      : Math.max(bestIdx - 1, 0);

    if (targetIdx === bestIdx) return;

    const targetEl = els[targetIdx];
    const targetTop = targetEl.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: targetTop, behavior: 'instant' });
  }, []);

  // NEW: Load queued articles from Copy Blogs (marked as is_added_to_automation = true),
  // scoped to the currently active language profile.
  const loadQueuedArticles = async () => {
    if (!activeProfile?.id) return;
    try {
      const { data: queuedArticles, error } = await supabase
        ?.from('copy_blogs_articles')
        ?.select('*')
        ?.eq('is_added_to_automation', true)
        ?.eq('profile_id', activeProfile.id)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      if (queuedArticles && queuedArticles?.length > 0) {
        // localStorage articles take priority — don't override them with stale Supabase records
        try {
          const lsQueue = JSON.parse(localStorage.getItem(getCopyBlogsQueueKey(activeProfile?.id)) || '[]');
          if (lsQueue.length > 0) return;
        } catch {}
        const formattedArticles = queuedArticles?.map((article) => ({
          _uid: crypto.randomUUID(),
          id: article?.id,
          focusKeyword: article?.focus_keyword || '',
          title: article?.title || '',
          shortDescription: article?.excerpt || '',
          referenceImageUrl: article?.featured_image_url || '',
          enableRecipeCard: true,
          seoTitle: '',
          permalink: '',
          metaDescription: article?.meta_description || article?.excerpt || '',
          secondaryKeywords: [],
          tags: [],
          category: article?.category || '',
          status: null,
          wpPostId: null,
          wpPostUrl: null,
          errorMessage: null,
          siteId: null,
          isFromCopyBlogs: true,
          sourceArticleUrl: article?.article_url
        }));

        setArticles(formattedArticles);
      }
    } catch (error) {
      console.error('Error loading queued articles:', error);
    }
  };

  const loadSites = async () => {
    const isCurrent = startAddBlogSitesRequest();
    try {
      // Check if user is authenticated first
      const { data: { user } } = await supabase?.auth?.getUser();

      if (!user) {
        console.warn('User not authenticated, skipping site load');
        return;
      }

      const sitesData = await getSites(activeProfile?.id);
      if (!isCurrent()) return;
      setSites(sitesData || []);
      // Auto-select first WordPress site
      if (sitesData?.length > 0) {
        setSelectedSite(sitesData?.[0]?.id);
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error loading sites:', error);
      // Only show alert if it's not an authentication error
      if (!error?.message?.includes('Not authenticated')) {
        alert(`Error loading sites: ${error?.message || 'Unknown error'}`);
      }
    }
  };

  // Load shared categories for all article blocks
  const loadSharedCategories = async (siteId) => {
    if (!siteId) {
      setSharedCategories([]);
      return;
    }

    setCategoriesLoading(true);
    try {
      // Reuse this site's cached categories when available (near-instant dropdown),
      // and silently refresh them in the background so they don't go stale.
      const result = await wordpressService?.fetchGymPlixCategories(siteId, { backgroundRefresh: true });
      if (result?.success) {
        const normalized = result?.categories?.map((cat) => ({
          id: cat?.id,
          name: cat?.name,
          slug: cat?.name?.toLowerCase()?.replace(/\s+/g, '-')
        }));
        setSharedCategories(normalized || []);
      } else {
        // fallback to cached fetch
        const fallback = await fetchWordPressCategories(siteId);
        setSharedCategories(fallback?.success ? fallback?.categories || [] : []);
      }
    } catch (error) {
      console.error('Error loading shared categories:', error);
      setSharedCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

const loadPrompts = async () => {
  const isCurrent = startAddBlogPromptsRequest();
  try {
    const allPrompts = await getAllPrompts(activeProfile?.id);
    if (!isCurrent()) return;

    // ✅ KEEP ALL PROMPTS (WordPress + Image + Tools)
    setPrompts(allPrompts || []);

    // Auto-select first WordPress article template ONLY for article generation
    const wordpressArticlePrompts = allPrompts?.filter(
      (p) => p?.promptType === 'wordpress article'
    );

    if (wordpressArticlePrompts?.length > 0) {
      setSelectedPrompt(wordpressArticlePrompts?.[0]?.id);
    }
  } catch (error) {
    if (!isCurrent()) return;
    console.error('Error loading prompts:', error);
    alert(`Error loading prompts: ${error?.message || 'Unknown error'}`);
  }
};


  const loadDrafts = async () => {
    try {
      const drafts = await getDraftArticles();
      if (drafts?.length > 0 && !location?.state?.importedArticle) {
        // Convert drafts to article format
        setArticles(drafts?.map((draft) => ({
          _uid: crypto.randomUUID(),
          id: draft?.id,
          focusKeyword: draft?.focusKeyword || '',
          title: draft?.title || '',
          shortDescription: draft?.shortDescription || '',
          enableRecipeCard: draft?.enableRecipeCard ?? true,
          seoTitle: draft?.seoTitle || '',
          permalink: draft?.permalink || '',
          metaDescription: draft?.metaDescription || '',
          secondaryKeywords: draft?.secondaryKeywords || [],
          tags: draft?.tags || [],
          status: draft?.status,
          wpPostId: draft?.wpPostId,
          wpPostUrl: draft?.wpPostUrl,
          errorMessage: draft?.errorMessage,
          siteId: draft?.siteId
        })));
      }
    } catch (error) {
      console.error('Error loading drafts:', error);
    }
  };

  // Debounced autosave function
  const debouncedAutosave = useCallback((articleIndex) => {
    if (autosaveTimerRef?.current) {
      clearTimeout(autosaveTimerRef?.current);
    }

    autosaveTimerRef.current = setTimeout(async () => {
      await saveDraft(articleIndex);
    }, 2000); // Save 2 seconds after last change
  }, []);

  const saveDraft = async (index) => {
    const article = articles?.[index];

    // CRITICAL FIX: Only save if focus keyword exists (required field)
    if (!article?.focusKeyword?.trim()) {
      console.log('Skipping autosave - focus keyword is required');
      return;
    }

    setIsSaving(true);
    try {
      const savedArticle = await saveDraftArticle({
        id: article?.id,
        siteId: selectedSite,
        focusKeyword: article?.focusKeyword,
        title: article?.title,
        shortDescription: article?.shortDescription,
        seoTitle: article?.seoTitle,
        metaDescription: article?.metaDescription,
        enableRecipeCard: article?.enableRecipeCard,
        promptId: selectedPrompt
      });

      // Update article with database ID
      updateArticle(index, { id: savedArticle?.id }, false);
      setLastSaved(new Date());
      hasUnsavedChanges.current = false;
    } catch (error) {
      console.error('Error saving draft:', error);
      // Don't show error to user during autosave - just log it
    } finally {
      setIsSaving(false);
    }
  };

  const saveAllDrafts = async () => {
    for (let i = 0; i < articles?.length; i++) {
      await saveDraft(i);
    }
  };

  const updateArticle = (index, updates, triggerAutosave = true) => {
    setArticles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated?.[index], ...updates };
      // Sync both the ref and localStorage immediately — no async gap, no race with beforeunload
      articlesRef.current = updated;
      try { localStorage.setItem('addpost_articles_cache', JSON.stringify(updated)); } catch {}
      return updated;
    });

    if (triggerAutosave) {
      hasUnsavedChanges.current = true;
      debouncedAutosave(index);
    }
  };

  // Apply a (possibly late) MidJourney/Discord result to the article that
  // originally sent the request, matched by its stable _uid — never by
  // array index. If that article was deleted before the response arrived,
  // the uid no longer matches anything and the result is silently dropped
  // instead of landing on whatever article now sits at the old index.
  const applyMJResultToArticle = (articleUid, updates) => {
    setArticles((prev) => {
      const idx = prev?.findIndex((a) => a?._uid === articleUid);
      if (idx === -1 || idx === undefined) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated?.[idx], ...updates };
      articlesRef.current = updated;
      try { localStorage.setItem('addpost_articles_cache', JSON.stringify(updated)); } catch {}
      return updated;
    });
  };

  const deleteArticle = async (index) => {
    if (articles?.length === 1) {
      return;
    }

    const article = articles?.[index];

    // NEW: If article is from Copy Blogs, restore it back to available state
    if (article?.isFromCopyBlogs && article?.id) {
      try {
        // Mark as NOT added to automation (restore to available in Copy Blogs)
        const { error } = await supabase?.
        from('copy_blogs_articles')?.
        update({ is_added_to_automation: false })?.
        eq('id', article?.id);

        if (error) throw error;
      } catch (error) {
        console.error('Error restoring article to Copy Blogs:', error);
        alert(`Error restoring article: ${error?.message}`);
        return;
      }
    }

    // Delete from database if it exists (non-Copy Blogs drafts)
    if (article?.id && !article?.isFromCopyBlogs) {
      try {
        await deleteDraftArticle(article?.id);
      } catch (error) {
        console.error('Error deleting draft:', error);
      }
    }

    // Remove from local state
    setArticles((prev) => prev?.filter((_, i) => i !== index));

    // Update localStorage for CopyBlogs articles
    if (article?.isFromCopyBlogs) {
      try {
        const queueKey = getCopyBlogsQueueKey(activeProfile?.id);
        const remaining = articles.filter((a, i) => i !== index && a.isFromCopyBlogs && !a.status);
        if (remaining.length > 0) {
          localStorage.setItem(queueKey, JSON.stringify(
            remaining.map(a => ({
              title: a.title, focusKeyword: a.focusKeyword,
              shortDescription: a.shortDescription, referenceImageUrl: a.referenceImageUrl,
              seoTitle: a.seoTitle, permalink: a.permalink, metaDescription: a.metaDescription,
              secondaryKeywords: a.secondaryKeywords, tags: a.tags,
              category: a.category, sourceArticleUrl: a.sourceArticleUrl,
            }))
          ));
        } else {
          localStorage.removeItem(queueKey);
        }
      } catch {}
    }
  };

  const addNewArticle = () => {
    setArticles((prev) => [...prev, makeEmptyArticle()]);
  };

  const generateTitlesForAll = async () => {
    try {
      const keywords = articles?.map((a) => a?.focusKeyword)?.filter(Boolean);
      if (keywords?.length === 0) {
        alert('Please enter focus keywords first');
        return;
      }

      const result = await openaiService?.generateArticleTitles(keywords);

      setArticles((prev) => prev?.map((article) => {
        const generated = result?.titles?.find((t) => t?.keyword === article?.focusKeyword);
        return generated ? { ...article, title: generated?.title } : article;
      }));


    } catch (error) {
      console.error('Error generating titles:', error);
      alert(`Error generating titles: ${error?.message || 'Failed to generate titles'}`);
    }
  };

// ── CSV Import ────────────────────────────────────────────────────────────────
const parseCSVRows = (text) => {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  // Parse a single CSV line respecting quoted fields
  const parseLine = (line) => {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const headers = parseLine(lines[0]).map((h) => h.toLowerCase().trim());
  const idx = (name) => headers.findIndex((h) => h === name);

  const titleIdx    = idx('title');
  const descIdx     = idx('description');
  const linkIdx     = idx('link');       // article URL → used for slug extraction
  const imageUrlIdx = idx('image url'); // Pinterest CDN image → reference image

  return lines.slice(1).map((line) => {
    const cols = parseLine(line);
    return {
      title:            cols[titleIdx]    || '',
      metaDescription:  cols[descIdx]     || '',
      articleUrl:       cols[linkIdx]     || '',
      referenceImageUrl: cols[imageUrlIdx] || '',
    };
  }).filter((row) => row.title || row.articleUrl || row.referenceImageUrl);
};

// Extract the last path segment of a URL and turn hyphens into spaces
const slugToKeyword = (url) => {
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean);
    return (parts[parts.length - 1] || '').replace(/-/g, ' ').trim();
  } catch {
    return '';
  }
};

const handleCSVFile = async (file) => {
  if (!file) return;
  if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
    alert('Please upload a valid .csv file.');
    return;
  }

  const text = await file.text();
  const rows = parseCSVRows(text);

  if (rows.length === 0) {
    alert('No valid rows found in the CSV. Make sure the file has columns: Title, Description, Link, Image URL');
    return;
  }

  setCsvImporting(true);

  const getFeaturedImage = async (url) => {
    try {
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const ogImage = doc.querySelector('meta[property="og:image"]')?.content;
      return ogImage || '';
    } catch (error) {
      return '';
    }
  };

  const imported = await Promise.all(
    rows.map(async (row) => {
      const focusKeyword = slugToKeyword(row.articleUrl);
      const referenceImageUrl = row.referenceImageUrl || (row.articleUrl ? await getFeaturedImage(row.articleUrl) : '');

      return {
        ...makeEmptyArticle(),
        title:            row.title,
        metaDescription:  row.metaDescription,
        focusKeyword,
        referenceImageUrl,
      };
    })
  );

  setArticles(imported);
  setTitlesGenerated(false);
  setCategoriesGenerated(false);
  setCsvImporting(false);

  // Reset the file input so the same file can be re-imported if needed
  if (csvInputRef.current) csvInputRef.current.value = '';
};
// ─────────────────────────────────────────────────────────────────────────────

const generateTitlesAndKeywordsForAll = async () => {
  setBulkAction('titles-keywords');

  try {
    const userId = await getUserId();
    let globalNextNumber = await getNextGlobalSlugNumberForUser(userId);
    const existingSlugs = new Set(
      articles?.map((a) => a?.permalink)?.filter(Boolean)
    );

    let bulkResults = [];
    try {
      const items = articles?.map((article, index) => ({
        index,
        title: article?.title || '',
        shortDescription: article?.shortDescription || '',
        focusKeyword: article?.focusKeyword || ''
      }));
      bulkResults = await generateSeoBulkFromEdge(items, selectedLanguage);
    } catch (err) {
      console.warn('Bulk SEO edge function failed, falling back to local generation:', err);
    }

    const byIndex = new Map(
      (bulkResults || []).map((item) => [item?.index, item])
    );

    for (let i = 0; i < articles?.length; i++) {
      const article = articles?.[i];
      const baseTitle = article?.title || article?.shortDescription || article?.focusKeyword || getLocalizedCopy(selectedLanguage).recipeWord;
      const bulk = byIndex.get(i) || {};

      const bulkKeyword = (bulk?.focus_keyword || bulk?.focusKeyword || '').trim();
      const finalFocusKeyword =
        bulkKeyword ||
        deriveFocusKeywordFromText(article?.focusKeyword || baseTitle) ||
        article?.focusKeyword ||
        baseTitle;

      // Local fallback must stay in the profile's language too — buildRankMathTitle's
      // patterns ("Best ... Recipe") are English-only word banks, so using them
      // under a non-English profile is what produced titles mixing the AI's
      // localized keyword with English filler words.
      const bulkSeoTitle = (bulk?.seo_title || bulk?.seoTitle || '').trim();
      const finalTitle = (selectedLanguage !== 'en' && bulkSeoTitle)
        ? bulkSeoTitle
        : (selectedLanguage === 'en'
            ? buildRankMathTitle(finalFocusKeyword, i)
            : buildLocalizedTitle(finalFocusKeyword, selectedLanguage, i));

      const bulkMeta = (bulk?.meta_description || bulk?.metaDescription || '').trim();
      const finalMeta = normalizeMetaDescription(
        bulkMeta,
        finalTitle,
        finalFocusKeyword,
        selectedLanguage
      );

      const baseSlug = (finalFocusKeyword || '')
        ?.toLowerCase()
        ?.replace(/[^a-z0-9\s-]/g, '')
        ?.replace(/\s+/g, '-')
        ?.replace(/-+/g, '-')
        ?.replace(/^-|-$/g, '');

      let slug = `${baseSlug}-${globalNextNumber}`;
      while (existingSlugs?.has(slug)) {
        globalNextNumber += 1;
        slug = `${baseSlug}-${globalNextNumber}`;
      }
      globalNextNumber += 1;
      existingSlugs.add(slug);

      updateArticle(i, {
        focusKeyword: finalFocusKeyword,
        title: finalTitle,
        seoTitle: finalTitle,
        metaDescription: finalMeta || article?.metaDescription || '',
        permalink: slug
      }, false);
    }

    setTitlesGenerated(true);
  } catch (error) {
    console.error('Error generating titles & keywords:', error);
    alert(`Error generating titles & keywords: ${error?.message || 'Failed to generate content'}`);
  } finally {
    setBulkAction(null);
  }
};


const generateKeywordsForAll = async () => {
  setBulkAction('keywords');

  console.log('CLICKED generateKeywordsForAll');

  const keywords = articles
    ?.map(a => a?.focusKeyword)
    ?.filter(k => k && k?.trim());

  if (!keywords?.length) {
    console.log('NO KEYWORDS');
    setBulkAction(null);
    return;
  }

  const result = await openaiService?.generateSecondaryKeywords(keywords);
  console.log('API RESULT:', result);

  setArticles(prev =>
    prev?.map(article => {
      const hit = result?.keywords?.find(
        k => k?.focus_keyword === article?.focusKeyword
      );

      return hit
        ? { ...article, secondaryKeywords: hit?.secondary_keywords }
        : article;
    })
  );

  setBulkAction(null);
};


const generateAllMJPrompts = async () => {
  const refs = articleRefs?.current?.filter(Boolean);

  if (!refs?.length) {
    alert('No articles available');
    return;
  }

  const hasKeywords = articles?.some(a => a?.focusKeyword?.trim());
  if (!hasKeywords) {
    alert('Please enter focus keywords first');
    return;
  }

  setBulkAction('mj');
  mjQueueAbortRef.current = false;

  try {
    // PART 1: Generate all MJ prompts sequentially (same existing logic)
    for (const ref of refs) {
      if (mjQueueAbortRef.current) break;
      await ref?.generateMJPrompt?.();
    }

    if (mjQueueAbortRef.current) { setBulkAction(null); return; }

    // PART 2-3: Send to Midjourney sequentially with progressive delays and max 10 concurrent
    const allRefs = articleRefs?.current?.filter(Boolean);
    const queueActiveRef = { current: 0 };

    // Wait until article i has image_main_url (max 10 min timeout)
    const waitForArticleImage = (index) => new Promise((resolve) => {
      if (articlesRef.current[index]?.image_main_url) { resolve(); return; }
      const start = Date.now();
      const id = setInterval(() => {
        if (mjQueueAbortRef.current || articlesRef.current[index]?.image_main_url) {
          clearInterval(id);
          resolve();
          return;
        }
        if (Date.now() - start > 600000) {
          clearInterval(id);
          resolve(); // unblock queue on timeout rather than stalling forever
        }
      }, 3000);
    });

    const jobPromises = [];

    for (let i = 0; i < allRefs.length; i++) {
      if (mjQueueAbortRef.current) break;

      // PART 3: Wait if at max 10 active jobs
      while (queueActiveRef.current >= 10) {
        if (mjQueueAbortRef.current) break;
        await new Promise(r => setTimeout(r, 2000));
      }
      if (mjQueueAbortRef.current) break;

      // Cyclic delay: 6s → 8s → 10s → 12s → 14s → 6s → 8s → …
      // Caps at 14s regardless of article count; never grows unbounded.
      const MJ_DELAYS = [6000, 8000, 10000, 12000, 14000];
      const delay = MJ_DELAYS[i % MJ_DELAYS.length];
      await new Promise(r => setTimeout(r, delay));
      if (mjQueueAbortRef.current) break;

      const idx = i;
      queueActiveRef.current++;

      const jobPromise = (async () => {
        try {
          await allRefs[idx]?.sendToMidjourney?.();
          // PART 4: Wait until image is fully processed and saved
          await waitForArticleImage(idx);
        } finally {
          queueActiveRef.current = Math.max(0, queueActiveRef.current - 1);
        }
      })();

      jobPromises.push(jobPromise);
    }

    // PART 4: Wait for all in-flight jobs to complete
    await Promise.allSettled(jobPromises);

    if (mjQueueAbortRef.current) { setBulkAction(null); return; }

    // PART 5: Validate every article before auto-sending
    const currentArticles = articlesRef.current;
    const missingList = [];
    currentArticles.forEach((a, i) => {
      const issues = [];
      if (!a?.title?.trim()) issues.push('title');
      if (!a?.metaDescription?.trim()) issues.push('metaDescription');
      if (!a?.selectedCategory) issues.push('category');
      if (!a?.image_main_url) issues.push('main image');
      if (issues.length > 0) missingList.push(`Article ${i + 1}: ${issues.join(', ')}`);
    });

    if (missingList.length > 0) {
      alert(`⚠️ Cannot auto-send. The following articles are incomplete:\n\n${missingList.map(e => `• ${e}`).join('\n')}`);
      setBulkAction(null);
      return;
    }

    // PART 6: Show countdown modal — auto-sends after 5 seconds
    setMjCountdownModal({ seconds: 5 });

  } catch (err) {
    console.error('MJ bulk error:', err);
    alert('Failed to generate some MJ prompts');
    setBulkAction(null);
  }
};

// NEW: Generate SEO Titles for All
const generateSEOTitlesForAll = async () => {
  setBulkAction('seo-titles');

  const refs = articleRefs?.current?.filter(Boolean);
  if (!refs?.length) {
    alert('No articles available');
    setBulkAction(null);
    return;
  }

  // Check if articles have required fields
  const hasRequiredFields = articles?.every(a => a?.focusKeyword?.trim() && a?.title?.trim());
  if (!hasRequiredFields) {
    alert('All articles must have focus keyword and title before generating SEO titles');
    setBulkAction(null);
    return;
  }

  try {
    for (const ref of refs) {
      await ref?.generateSEOTitle?.();
    }
    alert(`✅ SEO titles generated for ${refs?.length} article(s)!`);
  } catch (err) {
    console.error('SEO titles bulk error:', err);
    alert('Failed to generate some SEO titles');
  } finally {
    setBulkAction(null);
  }
};

// NEW: Generate 10 Focus Keywords for All
const generate10FocusKeywordsForAll = async () => {
  setBulkAction('10-keywords');

  const refs = articleRefs?.current?.filter(Boolean);
  if (!refs?.length) {
    alert('No articles available');
    setBulkAction(null);
    return;
  }

  // Check if articles have required fields
  const hasRequiredFields = articles?.every(a => a?.focusKeyword?.trim() && a?.title?.trim());
  if (!hasRequiredFields) {
    alert('All articles must have focus keyword and title before generating 10 focus keywords');
    setBulkAction(null);
    return;
  }

  try {
    for (const ref of refs) {
      await ref?.generate10FocusKeywords?.();
    }
    alert(`✅ 10 Focus Keywords generated for ${refs?.length} article(s)!`);
  } catch (err) {
    console.error('10 Focus Keywords bulk error:', err);
    alert('Failed to generate some focus keywords');
  } finally {
    setBulkAction(null);
  }
};

// Bulk categorize all articles - fetches categories directly like the bulb icon does
const categorizeAllArticles = async () => {
  if (!selectedSite) {
    alert('Please select a target WordPress site first');
    return;
  }

  setBulkAction('categorize');

  try {
    const refs = articleRefs?.current?.filter(Boolean);

    if (!refs?.length) {
      alert('No articles available to categorize');
      setBulkAction(null);
      return;
    }

    // Use already-loaded categories or fetch them fresh (same logic as bulb icon)
    let categoriesToUse = sharedCategories && sharedCategories.length > 0 ? sharedCategories : null;

    if (!categoriesToUse) {
      const result = await fetchWordPressCategories(selectedSite);
      if (result?.success && result?.categories?.length > 0) {
        categoriesToUse = result.categories;
        setSharedCategories(categoriesToUse);
      }
    }

    if (!categoriesToUse || categoriesToUse.length === 0) {
      alert('No categories found for this site. Please check site connection.');
      setBulkAction(null);
      return;
    }

    let successCount = 0;

    for (let i = 0; i < refs.length; i++) {
      const ref = refs[i];
      const article = articles?.[i];
      if (!article?.focusKeyword?.trim() && !article?.title?.trim()) continue;
      await ref?.autoSelectCategory?.(i, article, categoriesToUse);
      successCount++;
    }

    if (successCount > 0) {
      setCategoriesGenerated(true);
      setBulkAction('categorize-done');
      setTimeout(() => setBulkAction(null), 2500);
    } else {
      alert('⚠️ Could not categorize articles. Please ensure articles have content.');
      setBulkAction(null);
    }
  } catch (error) {
    console.error('Error categorizing articles:', error);
    alert('Failed to categorize articles. Please try again.');
    setBulkAction(null);
  }
};

// Apply one selected category to every currently available article
const setCategoryForAllArticles = (value) => {
  setGlobalCategorySelection(value);
  if (!value) return;

  const id = isNaN(parseInt(value, 10)) ? value : parseInt(value, 10);
  const refs = articleRefs?.current?.filter(Boolean);

  articles?.forEach((_, i) => updateArticle(i, { selectedCategory: id }));
  refs?.forEach((ref) => ref?.setSelectedCategory?.(id));
  setCategoriesGenerated(true);
};

// Shared single-article DB write, used by both "Send All to Queue" and the
// per-article "Send to Queue" button so the two stay identical.
const queueOneArticle = async (article, userId) => {
  // 🛡️ DUPLICATE GUARD: skip if an active row already exists for this
  // keyword + site so reconnecting and re-submitting never creates duplicates.
  const { data: existing } = await supabase
    ?.from('wp_articles')
    ?.select('id')
    ?.eq('user_id', userId)
    ?.eq('site_id', selectedSite)
    ?.eq('focus_keyword', article?.focusKeyword || '')
    ?.in('status', ['queued', 'processing'])
    ?.limit(1)
    ?.maybeSingle();

  if (existing?.id) {
    console.log(`⚠️ Skipping duplicate: "${article?.focusKeyword}" already queued (id=${existing.id})`);
    return { id: existing.id };
  }

  const { data: savedArticle, error: saveError } = await supabase?.from('wp_articles')?.insert({
      user_id: userId,
      site_id: selectedSite,
      profile_id: activeProfile?.id || null,
      language: selectedLanguage || 'en',

      focus_keyword: article?.focusKeyword || '',
      title: article?.title,
      short_description: article?.shortDescription,
      seo_title: article?.seoTitle || article?.title || null,
      meta_description: article?.metaDescription || null,
      permalink: article?.permalink || null,

      status: 'queued',
      // ✅ FIX: Store the publishing status (draft or publish)
      publish_status: publishingStatus,
      prompt_id: selectedPrompt,

      wordpress_category_id: article?.selectedCategory || null,

      enable_recipe_card: article?.enableRecipeCard,
      tags: article?.tags || [],
      secondary_keywords: article?.secondaryKeywords || [],

      image_main_url: article?.image_main_url || null,
      image_top_url: article?.image_top_url || null,
      image_middle_url: article?.image_middle_url || null,
      image_end_url: article?.image_end_url || null
    })?.select()?.single();

  if (saveError) throw saveError;

  return { id: savedArticle?.id };
};

const sendAllToBlogPosts = async () => {
  if (!selectedSite) {
    alert('Please select a target WordPress site first');
    return;
  }

  if (!articles?.length) {
    alert('No articles to send');
    return;
  }

  // Get button reference for loading state
  const button = document.querySelector('[data-send-queue-button]');
  const originalContent = button?.innerHTML;
  
  // Set loading state
  if (button) {
    button.disabled = true;
    button.innerHTML = '<svg class="w-4 h-4 mr-2 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Sending...';
  }

  try {
    const deletedDraftIds = [];
    const deletedCopyBlogsIds = [];

    // AUTO-GENERATE missing titles and descriptions before queuing
    for (let i = 0; i < articles?.length; i++) {
      const article = articles?.[i];

      // Auto-generate title if missing
      if (!article?.title || article?.title?.trim() === '') {
        try {
          const titleSuggestions = await openaiService?.generateSingleArticleTitleSuggestions(article?.focusKeyword);
          if (titleSuggestions && titleSuggestions?.length > 0) {
            updateArticle(i, { title: titleSuggestions?.[0] }, false);
            article.title = titleSuggestions?.[0];
          }
        } catch (error) {
          console.error(`Failed to generate title for article ${i + 1}:`, error);
        }
      }

      // Auto-generate description if missing
      if (!article?.shortDescription || article?.shortDescription?.trim() === '') {
        const description = generateShortDescriptionLocal(
          article?.title,
          article?.focusKeyword
        );
        if (description) {
          updateArticle(i, { shortDescription: description }, false);
          article.shortDescription = description;
        }
      }
    }

    // Validate all required fields per article, now that auto-generation has run
    const incompleteArticles = articles
      ?.map((article, i) => ({ index: i, missing: getMissingQueueFields(article) }))
      ?.filter((entry) => entry?.missing?.length > 0);

    if (incompleteArticles?.length > 0) {
      // Reset button state
      if (button) {
        button.disabled = false;
        button.innerHTML = originalContent;
      }
      const details = incompleteArticles
        ?.map(({ index, missing }) => `Article ${index + 1}\n${missing.map(m => `• Missing ${m}`).join('\n')}`)
        ?.join('\n\n');
      alert(`Some articles are incomplete:\n\n${details}\n\nPlease complete these fields before sending the articles to the queue.`, { title: 'Cannot Send to Queue' });
      return;
    }

    // Queue articles to database
    const userId = (await supabase?.auth?.getUser())?.data?.user?.id;

    for (let i = 0; i < articles?.length; i++) {
      const article = articles?.[i];

      const { id: savedId } = await queueOneArticle(article, userId);

      // Mark as sent in UI
      updateArticle(i, {
        status: 'queued',
        id: savedId
      }, false);

      // Track IDs for deletion
      if (article?.isFromCopyBlogs && article?.id) {
        deletedCopyBlogsIds?.push(article?.id);
      } else if (article?.id) {
        deletedDraftIds?.push(article?.id);
      }
    }

    // Flag Copy Blogs articles as sent
    if (deletedCopyBlogsIds?.length > 0) {
      const { error: flagError } = await supabase
        ?.from('copy_blogs_articles')
        ?.update({ is_added_to_automation: false })
        ?.in('id', deletedCopyBlogsIds);

      if (flagError) {
        console.error('Failed to restore Copy Blogs articles:', flagError);
      } else {
        console.log(`Restored ${deletedCopyBlogsIds?.length} Copy Blogs articles`);
      }
    }

    // Reset button state BEFORE showing success popup
    if (button) {
      button.disabled = false;
      button.innerHTML = originalContent;
    }

    // Success popup
    alert(`✅ ${articles?.length} article(s) sent to the Blog Posts Queue successfully!`);
    
    // Reset to empty form
    setArticles([makeEmptyArticle()]);
    try { localStorage.removeItem(getCopyBlogsQueueKey(activeProfile?.id)); } catch {}
    try { localStorage.removeItem('addpost_articles_cache'); } catch {}
    window.scrollTo(0, 0);

  } catch (error) {
    console.error('Error queuing articles:', error);
    
    // Reset button state on error
    if (button) {
      button.disabled = false;
      button.innerHTML = originalContent;
    }
    
    // Error popup
    alert(`❌ Failed to send articles to queue: ${error?.message || 'Unknown error'}`);
  }
};

// ❌ REMOVE generateAllArticles - No longer needed in AddBlogPost
// ❌ REMOVE generateArticle - No longer needed in AddBlogPost

// Send a single article (identified by its stable _uid, never by array
// index) to the Blog Posts Queue, reusing queueOneArticle — the exact same
// DB write sendAllToBlogPosts uses. On success, only that article is removed
// from the page; every other article is left completely untouched.
const sendArticleToQueue = async (uid) => {
  const index = articles?.findIndex((a) => a?._uid === uid);
  if (index === -1) return;
  const article = articles?.[index];

  if (!selectedSite) {
    alert('Please select a target WordPress site first');
    return;
  }

  try {
    // Auto-generate missing title/description, same as Send All to Queue
    let title = article?.title;
    if (!title || !title?.trim()) {
      try {
        const titleSuggestions = await openaiService?.generateSingleArticleTitleSuggestions(article?.focusKeyword);
        if (titleSuggestions && titleSuggestions?.length > 0) {
          title = titleSuggestions?.[0];
          updateArticle(index, { title }, false);
        }
      } catch (error) {
        console.error(`Failed to generate title for article ${index + 1}:`, error);
      }
    }

    let shortDescription = article?.shortDescription;
    if (!shortDescription || !shortDescription?.trim()) {
      shortDescription = generateShortDescriptionLocal(title, article?.focusKeyword);
      if (shortDescription) updateArticle(index, { shortDescription }, false);
    }

    // Validate required fields now that auto-generation has run
    const missing = getMissingQueueFields({ ...article, title, shortDescription });
    if (missing.length > 0) {
      alert(`Article ${index + 1} cannot be sent to the queue.\n\nMissing:\n${missing.map(m => `• ${m}`).join('\n')}\n\nPlease complete the missing fields and try again.`, { title: 'Cannot Send to Queue' });
      return;
    }

    const userId = (await supabase?.auth?.getUser())?.data?.user?.id;
    await queueOneArticle({ ...article, title, shortDescription }, userId);

    // Flag the Copy Blogs source article as sent, same as Send All to Queue
    if (article?.isFromCopyBlogs && article?.id) {
      const { error: flagError } = await supabase
        ?.from('copy_blogs_articles')
        ?.update({ is_added_to_automation: false })
        ?.eq('id', article?.id);

      if (flagError) {
        console.error('Failed to restore Copy Blogs article:', flagError);
      }
    }

    // Only now — after the queue write succeeded — remove this one article.
    // Match by _uid so removal can never mix up which card goes away.
    setArticles((prev) => {
      const remaining = prev.filter((a) => a?._uid !== uid);
      return remaining.length > 0 ? remaining : [makeEmptyArticle()];
    });

    if (article?.isFromCopyBlogs) {
      try {
        const queueKey = getCopyBlogsQueueKey(activeProfile?.id);
        const remaining = articles.filter((a) => a?._uid !== uid && a?.isFromCopyBlogs && !a?.status);
        if (remaining.length > 0) {
          localStorage.setItem(queueKey, JSON.stringify(
            remaining.map(a => ({
              title: a.title, focusKeyword: a.focusKeyword,
              shortDescription: a.shortDescription, referenceImageUrl: a.referenceImageUrl,
              seoTitle: a.seoTitle, permalink: a.permalink, metaDescription: a.metaDescription,
              secondaryKeywords: a.secondaryKeywords, tags: a.tags,
              category: a.category, sourceArticleUrl: a.sourceArticleUrl,
            }))
          ));
        } else {
          localStorage.removeItem(queueKey);
        }
      } catch {}
    }
  } catch (error) {
    console.error('Error queuing article:', error);
    alert(`❌ Failed to send article to queue: ${error?.message || 'Unknown error'}`);
    throw error;
  }
};

  const removeAllDetectedArticles = async () => {
    const detected = articles
      .map((a, i) => ({ article: a, index: i }))
      .filter(({ article }) => hasPorkOrAlcohol(article));

    if (detected.length === 0) return;

    for (const { article } of detected) {
      if (article?.isFromCopyBlogs && article?.id) {
        try {
          await supabase
            ?.from('copy_blogs_articles')
            ?.update({ is_added_to_automation: false })
            ?.eq('id', article?.id);
        } catch (err) {
          console.error('Error restoring article:', err);
        }
      } else if (article?.id && !article?.isFromCopyBlogs) {
        try {
          await deleteDraftArticle(article?.id);
        } catch (err) {
          console.error('Error deleting draft:', err);
        }
      }
    }

    const detectedUids = new Set(detected.map(({ article }) => article._uid));
    setArticles((prev) => {
      const remaining = prev.filter(a => !detectedUids.has(a._uid));
      return remaining.length > 0 ? remaining : [makeEmptyArticle()];
    });

    try {
      const queueKey = getCopyBlogsQueueKey(activeProfile?.id);
      const remaining = articles.filter(a => !detectedUids.has(a._uid) && a.isFromCopyBlogs && !a.status);
      if (remaining.length > 0) {
        localStorage.setItem(queueKey, JSON.stringify(
          remaining.map(a => ({
            title: a.title, focusKeyword: a.focusKeyword,
            shortDescription: a.shortDescription, referenceImageUrl: a.referenceImageUrl,
            seoTitle: a.seoTitle, permalink: a.permalink, metaDescription: a.metaDescription,
            secondaryKeywords: a.secondaryKeywords, tags: a.tags,
            category: a.category, sourceArticleUrl: a.sourceArticleUrl,
          }))
        ));
      } else {
        localStorage.removeItem(queueKey);
      }
    } catch {}
  };

  const handleResetAll = () => {
    // drop everything and show just one new empty article
    setArticles([makeEmptyArticle()]);
    try { localStorage.removeItem(getCopyBlogsQueueKey(activeProfile?.id)); } catch {}
    try { localStorage.removeItem('addpost_articles_cache'); } catch {}
    window.scrollTo(0, 0);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-white">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Add Blog Post</h1>
                  <p className="mt-2 text-gray-600">
                    Create AI-powered recipe articles with automatic SEO optimization
                  </p>
                  {articles?.filter((a) => a?.isFromCopyBlogs)?.length > 0 &&
                  <div className="mt-2 inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {articles?.filter((a) => a?.isFromCopyBlogs)?.length} article(s) queued from Copy Blogs
                    </div>
                  }
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  {isSaving &&
                  <div className="flex items-center text-sm text-gray-600">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </div>
                  }
                  {lastSaved && !isSaving &&
                  <div className="text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 inline mr-1 text-green-600" />
                      Last saved: {new Date(lastSaved)?.toLocaleTimeString()}
                    </div>
                  }
                  <Button
                    onClick={saveAllDrafts}
                    disabled={isSaving}
                    variant="outline"
                    className="flex items-center">

                    <Save className="w-4 h-4 mr-2" />
                    Save All Drafts
                  </Button>
                </div>
              </div>
            </div>

            {/* Global Controls */}
            <div className="rounded-lg shadow-sm p-6 mb-8 bg-[rgba(184, 219, 71, 1)] border-[3px] border-[rgba(135,190,25,1)] rounded-[22px]">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Global Settings</h2>
              
              {/* Row 1: Three inline selects */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prompt Template
                  </label>
                  <Select
                    value={selectedPrompt}
                    onChange={(value) => {
                      setSelectedPrompt(value);
                      setOpenDropdown(null);
                    }}
                    onOpenChange={(isOpen) => {
                      if (isOpen) {
                        setOpenDropdown('prompt');
                      } else if (openDropdown === 'prompt') {
                        setOpenDropdown(null);
                      }
                    }}
                    options={prompts?.map((prompt) => ({
                      value: prompt?.id,
                      label: prompt?.name
                    }))}
                    placeholder={prompts?.length === 0 ? "No templates available" : undefined}
                    className="w-full" />

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Site
                  </label>
                  <Select
                    value={selectedSite}
                    onChange={(value) => {
                      setSelectedSite(value);
                      setOpenDropdown(null);
                    }}
                    onOpenChange={(isOpen) => {
                      if (isOpen) {
                        setOpenDropdown('site');
                      } else if (openDropdown === 'site') {
                        setOpenDropdown(null);
                      }
                    }}
                    options={sites?.map((site) => ({
                      value: site?.id,
                      label: site?.siteName
                    }))}
                    placeholder={sites?.length === 0 ? "No sites available" : undefined}
                    className="w-full" />

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publishing Status
                  </label>
                  <Select
                    value={publishingStatus}
                    onChange={(value) => setPublishingStatus(value)}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'publish', label: 'Publish' }
                    ]}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Single Row: Global Actions */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-3 mt-4">
                {/* Hidden CSV file input */}
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleCSVFile(e.target.files?.[0])}
                />
                <Button
                  onClick={() => csvInputRef.current?.click()}
                  disabled={csvImporting}
                  className="w-full sm:w-auto h-10 px-5 bg-teal-100 hover:bg-teal-200 text-teal-800 shadow-md active:scale-[.98] flex items-center justify-center space-x-2"
                >
                  {csvImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Importing…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Import CSV</span>
                    </>
                  )}
                </Button>
                <div className="w-full sm:w-auto" title="Generation language is set by the active language profile — switch profiles in the sidebar to change it.">
                  <Select
                    value={selectedLanguage}
                    onChange={() => {}}
                    disabled
                    options={LANGUAGE_OPTIONS}
                    className="w-full sm:w-36 h-10"
                  />
                </div>
                <Button
                  onClick={generateTitlesAndKeywordsForAll}
                  disabled={bulkAction === 'titles-keywords'}
                  className="w-full sm:w-auto h-10 px-5 bg-pink-200 hover:bg-pink-300 text-gray-900 shadow-md active:scale-[.98] flex items-center justify-center space-x-2"
                >
                  {bulkAction === 'titles-keywords' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating…</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Generate Titles & Keywords to All</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={categorizeAllArticles}
                  disabled={bulkAction === 'categorize' || bulkAction === 'categorize-done'}
                  className={`w-full sm:w-auto h-10 px-5 font-semibold shadow-md active:scale-95 flex items-center justify-center space-x-2 transition-colors duration-300 ${
                    bulkAction === 'categorize-done'
                      ? 'bg-green-500 hover:bg-green-500 text-white'
                      : 'bg-amber-400 hover:bg-amber-500 text-[rgba(39,39,42,1)]'
                  }`}
                >
                  {bulkAction === 'categorize' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Categorizing…</span>
                    </>
                  ) : bulkAction === 'categorize-done' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Done!</span>
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4" />
                      <span>Auto-Categorize</span>
                    </>
                  )}
                </Button>
                <div className="w-full sm:w-auto flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Set Category for All:</span>
                  <Select
                    value={globalCategorySelection}
                    onChange={setCategoryForAllArticles}
                    options={sharedCategories?.map((cat) => ({
                      value: cat?.id?.toString(),
                      label: cat?.name
                    }))}
                    placeholder="Select Category"
                    className="w-full sm:w-44 h-10"
                  />
                </div>
                <Button
                  onClick={generateAllMJPrompts}
                  disabled={bulkAction === 'mj'}
                  className="w-full sm:w-auto h-10 px-5 bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-md active:scale-95 flex items-center justify-center space-x-2"
                >
                  {bulkAction === 'mj' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating MJ…</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Generate MJ ALL</span>
                    </>
                  )}
                </Button>
              </div>
              {/* Pork/Alcohol Detection */}
              {(() => {
                const detected = articles
                  .map((a, i) => ({ article: a, index: i }))
                  .filter(({ article }) => hasPorkOrAlcohol(article));
                if (detected.length === 0) return null;
                return (
                  <div className="mt-4 pt-4 border-t border-red-200">
                    <p className="text-sm font-semibold text-red-700 mb-2">Articles with pork/alcohol detected:</p>
                    <ul className="text-sm text-red-600 mb-3 space-y-0.5">
                      {detected.map(({ index }) => (
                        <li key={index}>Article {index + 1}</li>
                      ))}
                    </ul>
                    <Button
                      onClick={removeAllDetectedArticles}
                      className="h-10 px-5 bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md flex items-center justify-center"
                    >
                      Remove All Detected Articles
                    </Button>
                  </div>
                );
              })()}
            </div>

           {/* Article Blocks */}
{articles?.map((article, index) => (
  <div
    key={article?._uid || article?.id || index}
    ref={(el) => (articleDomRefs.current[index] = el)}
    style={{ scrollMarginTop: '80px' }}
  >
    <ArticleBlock
      ref={(el) => (articleRefs.current[index] = el)}
      article={article}
      index={index}
      onUpdate={updateArticle}
      onDelete={deleteArticle}
      sites={sites}
      prompts={prompts}
      isGenerating={isGenerating}
      selectedSite={selectedSite}
      articlesLength={articles?.length}
      existingSlugs={new Set(articles?.map((a) => a?.permalink).filter(Boolean))}
      sharedCategories={sharedCategories}
      sharedCategoriesLoading={categoriesLoading}
      mjGlobalBusy={mjActiveCount >= 10}
      onMJBusyChange={handleMJBusyChange}
      selectedLanguage={selectedLanguage}
      onSendToQueue={sendArticleToQueue}
      onApplyMJResult={applyMJResultToArticle}
    />
  </div>
))}


            {/* Bottom Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              {/* Reset All */}
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleResetAll}
              >
                Reset All
              </Button>

              {/* Add Article */}
              <Button
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900"
                onClick={addNewArticle}
              >
                + Add Article
              </Button>

            {/* Generate All */}
            <Button
              onClick={sendAllToBlogPosts}
              data-send-queue-button
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Send to Blog Posts Queue
            </Button>




            </div>
          </div>
        </main>
      </div>
      {/* Floating article navigation buttons */}
      <div style={{
        position: 'fixed',
        right: '20px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 1000,
      }}>
        {/* Scroll to top */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
          title="Scroll to top"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563',
            transition: 'background 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 11 12 5 6 11" />
            <polyline points="18 18 12 12 6 18" />
          </svg>
        </button>
        {/* Previous article */}
        <button
          onClick={() => snapToArticle(-1)}
          title="Previous article"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563',
            transition: 'background 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
        {/* Next article */}
        <button
          onClick={() => snapToArticle(1)}
          title="Next article"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563',
            transition: 'background 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {/* Scroll to bottom */}
        <button
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'instant' })}
          title="Scroll to bottom"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563',
            transition: 'background 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 6 12 12 18 6" />
            <polyline points="6 13 12 19 18 13" />
          </svg>
        </button>
      </div>
      {/* MJ Auto-Send Countdown Modal */}
      {mjCountdownModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: '16px',
              padding: '36px 40px', maxWidth: '420px', width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: '#111' }}>
              All images are ready
            </h2>
            <p style={{ fontSize: '15px', color: '#444', marginBottom: '24px', lineHeight: 1.5 }}>
              Articles will be sent to Blog Posts Queue in{' '}
              <strong style={{ color: '#16a34a', fontSize: '18px' }}>
                {mjCountdownModal.seconds}
              </strong>{' '}
              second{mjCountdownModal.seconds !== 1 ? 's' : ''}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  if (mjCountdownTimerRef.current) clearTimeout(mjCountdownTimerRef.current);
                  setMjCountdownModal(null);
                  setBulkAction(null);
                  sendAllToBlogPosts();
                }}
                style={{
                  padding: '10px 28px', borderRadius: '8px', border: 'none',
                  background: '#16a34a', color: '#fff', fontWeight: 700,
                  fontSize: '15px', cursor: 'pointer',
                }}
              >
                OK — Send Now
              </button>
              <button
                onClick={() => {
                  mjQueueAbortRef.current = true;
                  if (mjCountdownTimerRef.current) clearTimeout(mjCountdownTimerRef.current);
                  setMjCountdownModal(null);
                  setBulkAction(null);
                }}
                style={{
                  padding: '10px 28px', borderRadius: '8px', border: '2px solid #d1d5db',
                  background: '#fff', color: '#374151', fontWeight: 700,
                  fontSize: '15px', cursor: 'pointer',
                }}
              >
                WAIT — Send Manually
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarProvider>
  );
}
